import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ClientModal from './ClientModal';
import ClientDetail from './ClientDetail';
import PartnerApiKeys from './PartnerApiKeys';
import ClientsMonetico from './ClientsMonetico';
import EmandatClients from './EmandatClients';
import DataEsprotect from './DataEsprotect';
import SuiviConnect from './SuiviConnect';
import IaClients from './IaClients';
import ProspectionIa from './ProspectionIa';
import DataPro from './DataPro';
import RecherchePro from './RecherchePro';
import MailEsprotect from './MailEsprotect';
import BoiteMail from './BoiteMail';
import AdminAssistant from './AdminAssistant';
import UserManagement from './UserManagement';
import DocumentsManagement from './DocumentsManagement';
import ClientPortal from './ClientPortal';
import NotificationCenter from './NotificationCenter';
import EmailCenter from './EmailCenter';
import ProspectManagement from './ProspectManagement';
import InstallPWA from './InstallPWA';
import DevisManagement from './DevisManagement';
import FacturesManagement from './FacturesManagement';
import InfluenceursManagement from './InfluenceursManagement';
import PrestationsManagement from './PrestationsManagement';
import CahierDesCharges from './CahierDesCharges';
import Planning from './Planning';
import LeadsManagement from './LeadsManagement';
import CandidatsAssu from './CandidatsAssu';
import CompteAssu from './CompteAssu';
import ScriptAppel from './ScriptAppel';
import ScriptAppelAdmin from './ScriptAppelAdmin';
import TeamManagement from './TeamManagement';
import FichierPro from './FichierPro';
import EShopsProsper from './EShopsProsper';
import ProspectCEE from './ProspectCEE';
import LeadsCEEAgent from './LeadsCEEAgent';
import LeadsCEEAdmin from './LeadsCEEAdmin';
import CompagneCEE from './CompagneCEE';
import BasePerso from './BasePerso';
import Simulateur from './Simulateur';
import DataPacPv from './DataPacPv';
import EspaceCandidat from './EspaceCandidat';
import { requestNotificationPermission, notifyNewClient, notifyPaymentReceived, notifyClientSigned } from '../utils/notifications';
import './Dashboard.css';
import { Bot, Inbox, Database, FolderOpen, ClipboardList, Users, Upload, Calendar, Download, Target, FileText, Receipt, Wrench, User, Wallet, Folder, Phone, Megaphone, Mail, UsersRound, CreditCard, FileDown, Eye, Pencil, Trash2, Clock, Coins, BarChart3, Gift, Lightbulb, Search, Settings, Key, Copy, CheckCircle, XCircle, AlertTriangle, RefreshCw, Plus, Send, Calculator, GraduationCap, Shield, Wallet as WalletIcon, Globe } from 'lucide-react';

import { useRafraichissementAuto } from '../rafraichissementAuto';
// Ne garde que les chiffres d'une saisie (téléphone formaté, SIRET espacé…).
function digitsOnly(valeur) {
  return String(valeur || '').replace(/\D/g, '');
}

// Écritures équivalentes d'un même numéro, pour que 06… trouve +33 6… et inversement.
function phoneVariants(valeur) {
  const d = digitsOnly(valeur);
  if (!d) return [];
  const variantes = [d];
  if (d.startsWith('0033')) variantes.push('0' + d.slice(4));
  else if (d.startsWith('33') && d.length >= 10) variantes.push('0' + d.slice(2));
  else if (d.startsWith('0')) variantes.push('33' + d.slice(1));
  return variantes;
}

// Pôle métier d'un utilisateur (exclusif) : 'cee', 'pacpv' ou 'eshops'.
// Pour un agent : son drapeau is_agent_*. Pour un chef d'équipe : son cree_agents_*.
function computePole(u) {
  if (!u) return 'eshops';
  if (u.is_agent_cee || u.cree_agents_cee) return 'cee';
  if (u.is_agent_pacpv || u.cree_agents_pacpv) return 'pacpv';
  return 'eshops';
}

function Dashboard({ onLogout }) {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [viewingClient, setViewingClient] = useState(null);
  const [currentUser, setCurrentUser] = useState(() => {
    // Décoder le JWT immédiatement pour afficher la nav sans attendre l'API
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const u = { id: payload.id, role: payload.role, nom: payload.nom || '',
          is_agent_cee: payload.is_agent_cee || 0, is_agent_pacpv: payload.is_agent_pacpv || 0,
          cree_agents_cee: payload.cree_agents_cee || 0, cree_agents_pacpv: payload.cree_agents_pacpv || 0 };
        u.pole = computePole(u);
        return u;
      }
    } catch (e) {}
    return null;
  });
  // Recherche d'onglet : avec une trentaine d'onglets, retrouver le bon a l'oeil
  // est penible. Le filtre masque les boutons dont le libelle ne correspond pas.
  const [rechercheOnglet, setRechercheOnglet] = useState('');
  const barreOngletsRef = React.useRef(null);

  // Mise a jour automatique de la page (voir src/rafraichissementAuto.js)
  useRafraichissementAuto(() => { fetchClients(); fetchStats() }, { secondes: 45 });

  useEffect(() => {
    const q = rechercheOnglet.trim().toLowerCase();
    const boutons = document.querySelectorAll('.tabs button.tab, .admin-sidebar button.sidebar-item');
    boutons.forEach((b) => {
      const texte = (b.textContent || '').toLowerCase();
      b.style.display = (!q || texte.includes(q)) ? '' : 'none';
    });
    // on masque aussi les titres de section devenus vides
    document.querySelectorAll('.admin-sidebar .sidebar-section').forEach((sec) => {
      const visibles = Array.from(sec.querySelectorAll('button.sidebar-item'))
        .filter((b) => b.style.display !== 'none').length;
      sec.style.display = (!q || visibles > 0) ? '' : 'none';
    });
  });

  const [activeTab, setActiveTab] = useState(() => {
    // Sous-admin: toujours arriver sur Mon Équipe
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role === 'valideur') return 'compte-assu';
        if (payload.role === 'rh') return 'candidats-assu';
        if (payload.role === 'suivi_connect') return 'suivi-connect';
        if (payload.role === 'commercial_ia') return 'prospection-ia';
        if (payload.role === 'candidat_assu') return 'mon-espace';
        if (payload.role === 'sous_admin') return 'mon-equipe';
        if (payload.role === 'agent' && payload.is_agent_cee) return 'prospect-cee';
        if (payload.role === 'agent' && payload.is_agent_pacpv) return 'prospect-pacpv';
        if (payload.role === 'chef_equipe' && payload.cree_agents_cee) return 'leads-cee';
        if (payload.role === 'chef_equipe' && payload.cree_agents_pacpv) return 'leads-pacpv';
        if (payload.role === 'agent' || payload.role === 'commercial' || payload.role === 'chef_equipe') return 'eshops-prosper';
      }
    } catch (e) {}
    // Restaurer l'onglet actif depuis localStorage
    return localStorage.getItem('lastActiveTab') || 'clients';
  }); // 'clients' ou 'users'
  const [stats, setStats] = useState(null);
  const [filterPaye, setFilterPaye] = useState('tous'); // 'tous', 'paye', 'non_paye'
  const [filterSigne, setFilterSigne] = useState('tous'); // 'tous', 'signe', 'non_signe'
  const [showFilters, setShowFilters] = useState(false); // Afficher/masquer les filtres
  const [generatingInvoice, setGeneratingInvoice] = useState({}); // Suivi de la génération de facture par client
  const [stripePayments, setStripePayments] = useState([]); // Historique des paiements Stripe
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [generatingAccounts, setGeneratingAccounts] = useState(false); // Génération des comptes clients
  const [eshopStripePayments, setEshopStripePayments] = useState([]); // Paiements Stripe de l'e-shop owner
  const [loadingEshopStripePayments, setLoadingEshopStripePayments] = useState(false);
  const [stripeConfig, setStripeConfig] = useState(null);
  const [showStripeConfigModal, setShowStripeConfigModal] = useState(false);
  const [stripeApiKey, setStripeApiKey] = useState('');
  const [commissionsHistory, setCommissionsHistory] = useState([]); // Historique des commissions
  const [loadingCommissions, setLoadingCommissions] = useState(false);
  const [myBonuses, setMyBonuses] = useState([]); // Bonus reçus par l'agent
  const [financialStats, setFinancialStats] = useState(null); // Stats financières globales
  const [selectedUserFiche, setSelectedUserFiche] = useState(null); // Fiche utilisateur sélectionnée
  const [loadingUserFiche, setLoadingUserFiche] = useState(false); // Chargement de la fiche
  const [searchFiche, setSearchFiche] = useState(''); // Recherche fiche agent/commercial

  useEffect(() => {
    fetchCurrentUser();
    // Demander la permission pour les notifications
    requestNotificationPermission();
  }, []);

  // Agent CEE / PAC-PV : pas d'accès à Recherche Pro ni Importer Base -> rediriger vers son onglet pôle
  useEffect(() => {
    if (!currentUser) return;
    if (currentUser.pole === 'cee' && (activeTab === 'eshops-prosper' || activeTab === 'base-perso')) {
      setActiveTab('prospect-cee');
    } else if (currentUser.pole === 'pacpv' && (activeTab === 'eshops-prosper' || activeTab === 'base-perso')) {
      setActiveTab('prospect-pacpv');
    }
  }, [currentUser, activeTab]);

  // Permissions : si l'onglet actif a été masqué par l'admin, basculer vers un onglet visible
  useEffect(() => {
    if (!currentUser) return;
    const hidden = Array.isArray(currentUser.hidden_tabs)
      ? currentUser.hidden_tabs
      : (() => { try { return JSON.parse(currentUser.hidden_tabs || '[]'); } catch (e) { return []; } })();
    if (!hidden.length || currentUser.role === 'admin') return;
    if (!hidden.includes(activeTab)) return;
    const r = currentUser.role, pole = currentUser.pole;
    const candidates = [];
    if ((r === 'agent' || r === 'chef_equipe' || r === 'sous_admin' || r === 'commercial') && pole === 'eshops') candidates.push('eshops-prosper', 'base-perso');
    if (pole === 'cee') candidates.push('prospect-cee', 'leads-cee', 'planning-cee', 'script-cee');
    if (pole === 'pacpv') candidates.push('prospect-pacpv', 'leads-pacpv', 'planning-pacpv', 'script-pacpv');
    if ((r === 'agent' || r === 'chef_equipe' || r === 'sous_admin') && currentUser.has_extraction_access === 1) candidates.push('prospects-extraction');
    if ((r === 'agent' || r === 'chef_equipe' || r === 'sous_admin') && pole === 'eshops') candidates.push('planning', 'script-appel');
    if (r !== 'sous_admin') candidates.push('clients');
    if (r === 'agent' || r === 'commercial') candidates.push('compte');
    const firstVisible = candidates.find(k => !hidden.includes(k));
    if (firstVisible) setActiveTab(firstVisible);
  }, [currentUser, activeTab]);

  // Connexion WebSocket pour les notifications en temps réel
  useEffect(() => {
    if (!currentUser) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // Déterminer l'URL du WebSocket
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.host;
    const wsUrl = `${wsProtocol}//${wsHost}/ws?token=${token}`;

    console.log('🔌 Connexion WebSocket...', wsUrl);

    let ws = null;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;

    const connectWebSocket = () => {
      ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('✅ WebSocket connecté - Notifications en temps réel activées');
        reconnectAttempts = 0;
      };

      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 Message WebSocket reçu:', data);

          if (data.type === 'notification') {
            const notification = data.data;
            console.log('🔔 Nouvelle notification reçue:', notification);

            // Afficher la notification immédiatement
            if (Notification.permission === 'granted') {
              try {
                // Utiliser le Service Worker pour afficher la notification (fonctionne sur mobile)
                if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                  const registration = await navigator.serviceWorker.ready;
                  await registration.showNotification(notification.title, {
                    body: notification.message,
                    icon: '/icon-192.png',
                    badge: '/icon-192.png',
                    tag: `notif-${notification.id}`,
                    requireInteraction: notification.require_interaction,
                    vibrate: [200, 100, 200],
                    data: {
                      url: notification.url || '/',
                      notificationId: notification.id
                    }
                  });
                } else {
                  // Fallback : notification native
                  const notif = new Notification(notification.title, {
                    body: notification.message,
                    icon: '/icon-192.png',
                    badge: '/icon-192.png',
                    tag: `notif-${notification.id}`,
                    requireInteraction: notification.require_interaction,
                    vibrate: [200, 100, 200]
                  });

                  notif.onclick = () => {
                    window.focus();
                    if (notification.url && notification.url !== '/') {
                      window.location.href = notification.url;
                    }
                    notif.close();
                  };
                }
                console.log('✅ Notification affichée instantanément');
              } catch (error) {
                console.error('Erreur affichage notification:', error);
              }
            }
          }
        } catch (error) {
          console.error('Erreur parsing message WebSocket:', error);
        }
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket déconnecté:', event.code, event.reason);

        // Tenter de reconnecter si pas fermé volontairement
        if (reconnectAttempts < maxReconnectAttempts && event.code !== 1000) {
          reconnectAttempts++;
          console.log(`🔄 Tentative de reconnexion ${reconnectAttempts}/${maxReconnectAttempts} dans 5s...`);
          setTimeout(connectWebSocket, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('❌ Erreur WebSocket:', error);
      };
    };

    connectWebSocket();

    // Nettoyer à la déconnexion
    return () => {
      if (ws) {
        console.log('🔌 Fermeture WebSocket...');
        ws.close(1000, 'Component unmount');
      }
    };
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      // Client avec accès CRM (e-shop owner)
      if (currentUser.role === 'client' && currentUser.has_crm_access === 1) {
        fetchClients(); // Va utiliser /api/eshop-clients
        fetchStats();   // Va utiliser /api/eshop-stats
      }
      // Ni le valideur ni le RH n'ont accès aux clients : on ne les appelle pas.
      else if (['valideur', 'rh', 'candidat_assu', 'suivi_connect', 'commercial_ia'].includes(currentUser.role)) { /* rien à charger ici */ }
      // Admins et commerciaux
      else if (currentUser.role !== 'client') {
        fetchClients(); // Va utiliser /api/clients
        fetchStats();   // Va utiliser /api/stats
      }
      // Client simple sans CRM
      else {
        fetchClientData(); // Client normal qui voit juste sa fiche
      }
    }
  }, [currentUser]);

  useEffect(() => {
    if (activeTab === 'comptes' && currentUser && currentUser.role === 'admin') {
      fetchCommissionsHistory();
      fetchFinancialStats();
    }
  }, [activeTab, currentUser]);

  useEffect(() => {
    if (activeTab === 'compte' && currentUser && (currentUser.role === 'agent' || currentUser.role === 'commercial')) {
      fetchCommissionsHistory();
      fetchMyBonuses();
    }
  }, [activeTab, currentUser]);

  // Rafraîchissement automatique des stats toutes les 30 secondes pour agents/commerciaux/admin
  useEffect(() => {
    if (currentUser && !['client', 'valideur', 'rh', 'candidat_assu', 'suivi_connect', 'commercial_ia'].includes(currentUser.role)) {
      const interval = setInterval(() => {
        fetchStats();
        fetchClients();
      }, 30000); // 30 secondes

      return () => clearInterval(interval);
    }
  }, [currentUser]);

  // Fonction de rafraîchissement manuel
  const handleRefreshStats = () => {
    fetchStats();
    fetchClients();
    if (currentUser.role === 'admin') {
      fetchCommissionsHistory();
      fetchFinancialStats();
    } else if (currentUser.role === 'agent' || currentUser.role === 'commercial') {
      fetchCommissionsHistory();
      fetchMyBonuses();
    }
  };

  useEffect(() => {
    // Charger automatiquement les infos du client e-shop quand il ouvre l'onglet "Mon Compte"
    if (activeTab === 'mon-compte' && currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1) {
      if (!viewingClient && !showDetail) {
        fetchClientData();
      }
    }
    // Réinitialiser l'affichage des infos quand on retourne sur "Mon CRM"
    else if (activeTab === 'clients' && currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1) {
      if (showDetail || viewingClient) {
        setShowDetail(false);
        setViewingClient(null);
      }
    }
    // Charger la config et les paiements Stripe pour l'onglet "Mes Paiements Stripe"
    else if (activeTab === 'stripe-payments' && currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1) {
      // Réinitialiser les infos personnelles pour ne pas les afficher dans cet onglet
      if (showDetail || viewingClient) {
        setShowDetail(false);
        setViewingClient(null);
      }
      fetchEshopStripeConfig();
      fetchEshopStripePayments();
    }
  }, [activeTab, currentUser, viewingClient, showDetail]);

  // Sauvegarder l'onglet actif dans localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem('lastActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    const terme = searchTerm.trim().toLowerCase();
    const termeChiffres = digitsOnly(terme);
    const termesNumeriques = phoneVariants(termeChiffres);

    let filtered = clients.filter(client => {
      if (!terme) return true;

      const texte = [
        client.numero_client,
        client.nom,
        client.prenom,
        `${client.nom || ''} ${client.prenom || ''}`,
        `${client.prenom || ''} ${client.nom || ''}`,
        client.email,
        client.entreprise,
        client.adresse,
        client.telephone,
        client.numero_siret
      ].map(v => String(v || '').toLowerCase()).join(' | ');

      if (texte.includes(terme)) return true;

      // Recherche sur les chiffres seuls : tolère les formats saisis
      // (06 12 34…, +33 6…, SIRET espacé) des deux côtés de la comparaison.
      if (termeChiffres.length >= 3) {
        const numeros = [
          ...phoneVariants(client.telephone),
          digitsOnly(client.numero_siret),
          digitsOnly(client.numero_client)
        ];
        if (numeros.some(n => n && termesNumeriques.some(t => n.includes(t)))) return true;
      }

      return false;
    });

    // Filtre par statut de paiement
    if (filterPaye === 'paye') {
      filtered = filtered.filter(client => client.paye === 1 || client.paye === true);
    } else if (filterPaye === 'non_paye') {
      filtered = filtered.filter(client => client.paye === 0 || client.paye === false);
    }

    // Filtre par statut de signature
    if (filterSigne === 'signe') {
      filtered = filtered.filter(client => client.client_signe === 1 || client.client_signe === true);
    } else if (filterSigne === 'non_signe') {
      filtered = filtered.filter(client => client.client_signe === 0 || client.client_signe === false);
    }

    setFilteredClients(filtered);
  }, [searchTerm, clients, filterPaye, filterSigne]);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser({ ...response.data, pole: computePole(response.data) });
    } catch (error) {
      console.error('Erreur lors du chargement de l\'utilisateur:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');

      // Déterminer l'endpoint selon le type d'utilisateur
      let endpoint = '/api/clients';
      if (currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1) {
        endpoint = '/api/eshop-clients'; // E-shop owner voit ses propres clients
      }

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');

      // Déterminer l'endpoint selon le type d'utilisateur
      let endpoint = '/api/stats';
      if (currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1) {
        endpoint = '/api/eshop-stats'; // E-shop owner voit ses propres stats
      }

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
  };

  const fetchClientData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/client/my-info', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewingClient(response.data);
      setShowDetail(true);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données client:', error);
      setLoading(false);
    }
  };

  const fetchStripePayments = async () => {
    setLoadingPayments(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/stripe-payments/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripePayments(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des paiements Stripe:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const fetchCommissionsHistory = async () => {
    setLoadingCommissions(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/commissions/history', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommissionsHistory(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique des commissions:', error);
    } finally {
      setLoadingCommissions(false);
    }
  };

  const fetchMyBonuses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/my-bonuses', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyBonuses(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des bonus:', error);
    }
  };

  const fetchFinancialStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/financial-stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFinancialStats(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques financières:', error);
    }
  };

  const fetchUserFiche = async (userId) => {
    setLoadingUserFiche(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/users/${userId}/fiche`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUserFiche(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de la fiche:', error);
      alert('Erreur lors du chargement de la fiche utilisateur');
    } finally {
      setLoadingUserFiche(false);
    }
  };

  const handleMarkCommissionPaidAgent = async (commissionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/commissions/mark-paid-agent/${commissionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchCommissionsHistory();
      fetchFinancialStats();

      alert(response.data.message || 'Commission agent marquée comme payée');
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.error || 'Erreur lors du marquage');
    }
  };

  const handleMarkCommissionPaidCommercial = async (commissionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/commissions/mark-paid-commercial/${commissionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchCommissionsHistory();
      fetchFinancialStats();

      alert(response.data.message || 'Commission commercial marquée comme payée');
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.error || 'Erreur lors du marquage');
    }
  };

  const handleMarkCommissionPaid = async (commissionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/api/commissions/mark-paid/${commissionId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchCommissionsHistory();
      fetchFinancialStats();

      alert(response.data.message || 'Toutes les commissions marquées comme payées');
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.error || 'Erreur lors du marquage');
    }
  };

  const handleDeleteCommission = async (commissionId, clientNom) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer cette commission pour "${clientNom}" ?\n\nCette action est irréversible.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/commissions/${commissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchCommissionsHistory();
      fetchFinancialStats();

      alert('Commission supprimée avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const handleMigrateCommissions = async () => {
    if (!window.confirm('Voulez-vous importer l\'historique des clients déjà payés dans les commissions ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/commissions/migrate-history', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`Migration réussie ! ${response.data.count} commission(s) importée(s)`);

      // Recharger l'historique et les stats
      fetchCommissionsHistory();
      fetchStats();
    } catch (error) {
      console.error('Erreur lors de la migration:', error);
      alert('Erreur lors de la migration de l\'historique');
    }
  };

  const fetchEshopStripeConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/eshop-stripe-config', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStripeConfig(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement de la config Stripe:', error);
    }
  };

  const fetchEshopStripePayments = async () => {
    setLoadingEshopStripePayments(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/eshop-stripe-payments', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEshopStripePayments(response.data.payments || []);
    } catch (error) {
      console.error('Erreur lors du chargement des paiements Stripe:', error);
      if (error.response?.status === 401) {
        alert('Clé API Stripe invalide. Veuillez vérifier votre configuration.');
      }
    } finally {
      setLoadingEshopStripePayments(false);
    }
  };

  const handleSaveStripeConfig = async () => {
    if (!stripeApiKey || !stripeApiKey.startsWith('sk_')) {
      alert('Veuillez entrer une clé API Stripe valide (commence par sk_)');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/eshop-stripe-config', {
        stripe_api_key: stripeApiKey
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Configuration Stripe enregistrée avec succès !');
      setShowStripeConfigModal(false);
      setStripeApiKey('');
      fetchEshopStripeConfig();
      fetchEshopStripePayments();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la config Stripe:', error);
      alert('Erreur lors de la sauvegarde de la configuration');
    }
  };

  const handleGenerateClientAccounts = async () => {
    if (!window.confirm('Voulez-vous générer automatiquement des comptes pour tous les clients payés qui n\'en ont pas encore ?')) {
      return;
    }

    setGeneratingAccounts(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/admin/generate-client-accounts', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { results } = response.data;

      let message = `✅ Génération terminée!\n\n`;
      message += `✓ ${results.success.length} comptes créés avec succès\n`;
      if (results.errors.length > 0) {
        message += `✗ ${results.errors.length} erreurs\n\n`;
      }

      if (results.success.length > 0) {
        message += `\nIdentifiants créés:\n`;
        results.success.forEach((account, index) => {
          if (index < 10) { // Afficher les 10 premiers
            message += `\n${account.clientName}:\n`;
            message += `  Login: ${account.username}\n`;
            message += `  Mot de passe: ${account.password}\n`;
          }
        });
        if (results.success.length > 10) {
          message += `\n... et ${results.success.length - 10} autres`;
        }
      }

      alert(message);

      // Rafraîchir les clients pour voir les comptes créés
      fetchClients();
    } catch (error) {
      console.error('Erreur lors de la génération des comptes:', error);
      alert('❌ Erreur lors de la génération des comptes: ' + (error.response?.data?.error || error.message));
    } finally {
      setGeneratingAccounts(false);
    }
  };

  const handleAddClient = () => {
    setEditingClient(null);
    setShowModal(true);
  };

  const [importingClients, setImportingClients] = useState(false);
  const handleImportClientsCsv = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!window.confirm(`Importer "${file.name}" ? Les doublons (email ou téléphone déjà existants) seront ignorés.`)) return;

    setImportingClients(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('token');
      const res = await fetch('/api/clients/import-csv', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur import');
      alert(
        `Import terminé !\n\n` +
        `✓ Importés : ${data.imported}\n` +
        `⊘ Doublons ignorés : ${data.duplicates}\n` +
        `✗ Erreurs : ${data.errors}\n` +
        `Total lignes : ${data.total}\n\n` +
        `Colonnes détectées : ${(data.columns || []).join(', ')}`
      );
      await fetchClients();
    } catch (err) {
      alert('Erreur import : ' + err.message);
    } finally {
      setImportingClients(false);
    }
  };

  const handleViewClient = (client) => {
    setViewingClient(client);
    setShowDetail(true);
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
    setShowModal(true);
    setShowDetail(false);
  };

  const handleDeleteClient = async (id, clientName = '') => {
    const message = clientName
      ? `Êtes-vous sûr de vouloir supprimer ce client "${clientName}" ?`
      : 'Êtes-vous sûr de vouloir supprimer ce client ?';
    if (!window.confirm(message)) {
      return;
    }

    try{
      const token = localStorage.getItem('token');

      // Déterminer l'endpoint selon le type d'utilisateur
      let endpoint = `/api/clients/${id}`;
      if (currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1) {
        endpoint = `/api/eshop-clients/${id}`; // E-shop owner supprime son propre client
      }

      await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchClients();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression du client');
    }
  };

  const handleGenerateInvoice = async (clientId) => {
    const today = new Date().toISOString().split('T')[0];
    const datePaiement = window.prompt(
      'Date à laquelle le client a payé (la facture sera émise à cette date) :\nFormat AAAA-MM-JJ',
      today
    );
    if (!datePaiement) return;

    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePaiement)) {
      alert('Format de date invalide. Utilisez AAAA-MM-JJ (ex: 2026-04-15)');
      return;
    }

    const modePaiement = window.prompt(
      'Mode de paiement (virement, cb, especes, cheque, prelevement, autre) :',
      'virement'
    );
    if (!modePaiement) return;

    setGeneratingInvoice(prev => ({ ...prev, [clientId]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/clients/${clientId}/facture`,
        {
          date_paiement: datePaiement,
          mode_paiement: modePaiement,
          reference_paiement: ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`Facture ${response.data.numeroFacture} générée avec succès!`);

      // Télécharger automatiquement la facture
      const pdfResponse = await axios.get(
        `/api/factures/by-numero/${response.data.numeroFacture}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${response.data.numeroFacture}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Erreur lors de la génération de la facture:', error);
      alert(error.response?.data?.error || 'Erreur lors de la génération de la facture');
    } finally {
      setGeneratingInvoice(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const handleGenerateBonCommande = async (clientId) => {
    if (!window.confirm('Voulez-vous générer un bon de commande pour ce client ?')) {
      return;
    }

    setGeneratingInvoice(prev => ({ ...prev, [`bdc_${clientId}`]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/clients/${clientId}/bon-commande`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert(`Bon de commande ${response.data.numeroBDC} généré avec succès!`);

      // Télécharger automatiquement le PDF
      const pdfResponse = await axios.get(
        `/api/bons-commande/${response.data.numeroBDC}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${response.data.numeroBDC}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Erreur lors de la génération du bon de commande:', error);
      alert(error.response?.data?.error || 'Erreur lors de la génération du bon de commande');
    } finally {
      setGeneratingInvoice(prev => ({ ...prev, [`bdc_${clientId}`]: false }));
    }
  };

  const handleSaveClient = async (clientData) => {
    try {
      const token = localStorage.getItem('token');

      // Déterminer les endpoints selon le type d'utilisateur
      const isEshopOwner = currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1;

      if (editingClient) {
        const putEndpoint = isEshopOwner
          ? `/api/eshop-clients/${editingClient.id}`
          : `/api/clients/${editingClient.id}`;

        await axios.put(putEndpoint, clientData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Notification si le client a payé
        if (clientData.paye && !editingClient.paye) {
          notifyPaymentReceived(clientData.nom, clientData.montant_paye || 0);
        }

        // Notification si le client a signé
        if (clientData.client_signe && !editingClient.client_signe) {
          notifyClientSigned(clientData.nom);
        }
      } else {
        const postEndpoint = isEshopOwner ? '/api/eshop-clients' : '/api/clients';

        await axios.post(postEndpoint, clientData, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Notification pour nouveau client
        notifyNewClient(clientData.nom);
      }

      // Fermer le modal immédiatement après succès
      setShowModal(false);

      // Rafraîchir les données (sans bloquer en cas d'erreur)
      try {
        await fetchClients();
        await fetchStats();
      } catch (refreshError) {
        console.error('Erreur lors du rafraîchissement des données:', refreshError);
        // Ne pas bloquer l'utilisateur, les données seront rafraîchies au prochain chargement
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      throw error;
    }
  };

  const getCurrentDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('fr-FR', options);
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: { text: 'Administrateur', class: 'badge-admin' },
      agent: { text: 'Agent Téléphonique', class: 'badge-agent' },
      commercial: { text: 'Directeur Commercial', class: 'badge-commercial' },
      chef_equipe: { text: 'Chef d\'équipe', class: 'badge-chef-equipe' },
      sous_admin: { text: 'Sous-Admin', class: 'badge-chef-equipe' },
      superviseur: { text: 'Superviseur', class: 'badge-superviseur' }
    };
    return badges[role] || { text: role, class: 'badge-default' };
  };

  // Onglets masqués par l'admin pour cet utilisateur (permissions)
  const hiddenTabs = Array.isArray(currentUser?.hidden_tabs)
    ? currentUser.hidden_tabs
    : (() => { try { return JSON.parse(currentUser?.hidden_tabs || '[]'); } catch (e) { return []; } })();
  const tabHidden = (k) => hiddenTabs.includes(k);

  return (
    <div className="dashboard">
      {activeTab !== 'simulateur' && <header className="dashboard-header">
        <div className="header-content">
          {currentUser?.role !== 'admin' && <div className="header-spacer"></div>}
          <div className={currentUser?.role === 'admin' ? 'header-brand-left' : 'header-brand'}>
            <img src="/logo-eshops-pro.png" alt="E Shops PRO" className="header-logo" />
            <p className="date">{getCurrentDate()}</p>
          </div>
          <div className="header-right">
            {currentUser && (
              <div className="user-info">
                <span className="user-name">{currentUser.nom}</span>
                <span className={`role-badge ${getRoleBadge(currentUser.role).class}`}>
                  {getRoleBadge(currentUser.role).text}
                </span>
              </div>
            )}
            <button onClick={onLogout} className="btn-logout">
              Déconnexion
            </button>
          </div>
        </div>
      </header>}

      {/* Sidebar pour Admin */}
      {activeTab !== 'simulateur' && currentUser && currentUser.role === 'admin' && (
        <aside className="admin-sidebar">
          <div style={{ padding: '10px 12px 4px' }}>
            <input
              type="search"
              value={rechercheOnglet}
              onChange={(e) => setRechercheOnglet(e.target.value)}
              placeholder="Rechercher un menu…"
              aria-label="Rechercher un menu"
              style={{ width: '100%', padding: '7px 11px', border: '1px solid #d5d5e0',
                       borderRadius: 5, fontSize: 13 }}
            />
          </div>
          <div className="sidebar-section">
            <h4 className="sidebar-title">IA E-Shops</h4>
            <button
              className={`sidebar-item ${activeTab === 'mon-assistant' ? 'active' : ''}`}
              onClick={() => setActiveTab('mon-assistant')}
            >
              <span className="sidebar-icon"><Bot size={18} /></span>
              <span>Mon assistant</span>
            </button>
          </div>
          <div className="sidebar-section">
            <h4 className="sidebar-title">E-Shops</h4>
            {currentUser.role === 'admin' && !tabHidden('ia-clients') && (
              <button
                className={`sidebar-item ${activeTab === 'ia-clients' ? 'active' : ''}`}
                onClick={() => setActiveTab('ia-clients')}
              >
                <span className="sidebar-icon"><UsersRound size={18} /></span>
                <span>Clients IA Shops</span>
              </button>
            )}
            {currentUser.role === 'admin' && !tabHidden('prospection-ia') && (
              <button
                className={`sidebar-item ${activeTab === 'prospection-ia' ? 'active' : ''}`}
                onClick={() => setActiveTab('prospection-ia')}
              >
                <span className="sidebar-icon"><Search size={18} /></span>
                <span>Prospection ia</span>
              </button>
            )}
            <button
              className={`sidebar-item ${activeTab === 'clients' ? 'active' : ''}`}
              onClick={() => setActiveTab('clients')}
            >
              <span className="sidebar-icon"><ClipboardList size={18} /></span>
              <span>Gestion Clients</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'eshops-prosper' ? 'active' : ''}`}
              onClick={() => setActiveTab('eshops-prosper')}
            >
              <span className="sidebar-icon"><Search size={18} /></span>
              <span>Recherche Pro</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'devis' ? 'active' : ''}`}
              onClick={() => setActiveTab('devis')}
            >
              <span className="sidebar-icon"><FileText size={18} /></span>
              <span>Devis</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'factures' ? 'active' : ''}`}
              onClick={() => setActiveTab('factures')}
            >
              <span className="sidebar-icon"><Receipt size={18} /></span>
              <span>Factures</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'prestations' ? 'active' : ''}`}
              onClick={() => setActiveTab('prestations')}
            >
              <span className="sidebar-icon"><Wrench size={18} /></span>
              <span>Prestations</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'clients-monetico' ? 'active' : ''}`}
              onClick={() => setActiveTab('clients-monetico')}
            >
              <span className="sidebar-icon"><CreditCard size={18} /></span>
              <span>Clients Monetico</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'suivi-connect' ? 'active' : ''}`}
              onClick={() => setActiveTab('suivi-connect')}
            >
              <span className="sidebar-icon"><Globe size={18} /></span>
              <span>Suivi E-SHOPS CONNECT</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'influenceurs' ? 'active' : ''}`}
              onClick={() => setActiveTab('influenceurs')}
            >
              <span className="sidebar-icon"><User size={18} /></span>
              <span>Influenceurs</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'simulateur' ? 'active' : ''}`}
              onClick={() => setActiveTab('simulateur')}
            >
              <span className="sidebar-icon"><Calculator size={18} /></span>
              <span>Simulateur</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'boite-mail' ? 'active' : ''}`}
              onClick={() => setActiveTab('boite-mail')}
            >
              <span className="sidebar-icon"><Inbox size={18} /></span>
              <span>Boite de reception</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'prospects' ? 'active' : ''}`}
              onClick={() => setActiveTab('prospects')}
            >
              <span className="sidebar-icon"><Users size={18} /></span>
              <span>Data Pro</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'prospects-extraction' ? 'active' : ''}`}
              onClick={() => setActiveTab('prospects-extraction')}
            >
              <span className="sidebar-icon"><Upload size={18} /></span>
              <span>Prospects Extraction</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'base-perso' ? 'active' : ''}`}
              onClick={() => setActiveTab('base-perso')}
            >
              <span className="sidebar-icon"><FolderOpen size={18} /></span>
              <span>Importer Base</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'leads' ? 'active' : ''}`}
              onClick={() => setActiveTab('leads')}
            >
              <span className="sidebar-icon"><Target size={18} /></span>
              <span>Leads</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'planning' ? 'active' : ''}`}
              onClick={() => setActiveTab('planning')}
            >
              <span className="sidebar-icon"><Calendar size={18} /></span>
              <span>Planning</span>
            </button>
          </div>

          <div className="sidebar-section">
            <h4 className="sidebar-title">CEE</h4>
            <button
              className={`sidebar-item ${activeTab === 'prospect-cee' ? 'active' : ''}`}
              onClick={() => setActiveTab('prospect-cee')}
            >
              <span className="sidebar-icon"><Search size={18} /></span>
              <span>Prospect CEE</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'leads-cee-admin' ? 'active' : ''}`}
              onClick={() => setActiveTab('leads-cee-admin')}
            >
              <span className="sidebar-icon"><Target size={18} /></span>
              <span>Leads CEE</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'compagne-cee' ? 'active' : ''}`}
              onClick={() => setActiveTab('compagne-cee')}
            >
              <span className="sidebar-icon"><Megaphone size={18} /></span>
              <span>Compagne CEE</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'planning-cee' ? 'active' : ''}`}
              onClick={() => setActiveTab('planning-cee')}
            >
              <span className="sidebar-icon"><Calendar size={18} /></span>
              <span>Planning CEE</span>
            </button>
          </div>

          <div className="sidebar-section">
            <h4 className="sidebar-title">PAC / PV</h4>
            <button
              className={`sidebar-item ${activeTab === 'prospect-pacpv' ? 'active' : ''}`}
              onClick={() => setActiveTab('prospect-pacpv')}
            >
              <span className="sidebar-icon"><Search size={18} /></span>
              <span>Prospect PAC/PV</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'leads-pacpv' ? 'active' : ''}`}
              onClick={() => setActiveTab('leads-pacpv')}
            >
              <span className="sidebar-icon"><Target size={18} /></span>
              <span>Leads PAC/PV</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'planning-pacpv' ? 'active' : ''}`}
              onClick={() => setActiveTab('planning-pacpv')}
            >
              <span className="sidebar-icon"><Calendar size={18} /></span>
              <span>Planning PAC/PV</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'script-pacpv' ? 'active' : ''}`}
              onClick={() => setActiveTab('script-pacpv')}
            >
              <span className="sidebar-icon"><Phone size={18} /></span>
              <span>Script PAC/PV</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'data-pacpv' ? 'active' : ''}`}
              onClick={() => setActiveTab('data-pacpv')}
            >
              <span className="sidebar-icon"><Database size={18} /></span>
              <span>Data PAC PV</span>
            </button>
          </div>

          <div className="sidebar-section">
            <h4 className="sidebar-title">Assurance</h4>
            <button
              className={`sidebar-item ${activeTab === 'cahier-charges' ? 'active' : ''}`}
              onClick={() => setActiveTab('cahier-charges')}
            >
              <span className="sidebar-icon"><Download size={18} /></span>
              <span>Candidatures</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'candidats-assu' ? 'active' : ''}`}
              onClick={() => setActiveTab('candidats-assu')}
            >
              <span className="sidebar-icon"><GraduationCap size={18} /></span>
              <span>Candidat Assu</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'compte-assu' ? 'active' : ''}`}
              onClick={() => setActiveTab('compte-assu')}
            >
              <span className="sidebar-icon"><WalletIcon size={18} /></span>
              <span>Compte Assu</span>
            </button>
          </div>

          <div className="sidebar-section">
            <h4 className="sidebar-title">ES-Protect</h4>
            <button
              className={`sidebar-item ${activeTab === 'data-esprotect' ? 'active' : ''}`}
              onClick={() => setActiveTab('data-esprotect')}
            >
              <span className="sidebar-icon"><Database size={18} /></span>
              <span>Data ES-PROTECT</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'mail-esprotect' ? 'active' : ''}`}
              onClick={() => setActiveTab('mail-esprotect')}
            >
              <span className="sidebar-icon"><Mail size={18} /></span>
              <span>Mail ES-PROTECT</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'emandat' ? 'active' : ''}`}
              onClick={() => setActiveTab('emandat')}
            >
              <span className="sidebar-icon"><Shield size={18} /></span>
              <span>Dossiers E-Mandat</span>
            </button>
          </div>

          <div className="sidebar-section">
            <h4 className="sidebar-title">Administration</h4>
            <button
              className={`sidebar-item ${activeTab === 'users' ? 'active' : ''}`}
              onClick={() => setActiveTab('users')}
            >
              <span className="sidebar-icon"><User size={18} /></span>
              <span>Utilisateurs</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'comptes' ? 'active' : ''}`}
              onClick={() => setActiveTab('comptes')}
            >
              <span className="sidebar-icon"><Wallet size={18} /></span>
              <span>Comptes</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'documents' ? 'active' : ''}`}
              onClick={() => setActiveTab('documents')}
            >
              <span className="sidebar-icon"><Folder size={18} /></span>
              <span>Documents</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'script-admin' ? 'active' : ''}`}
              onClick={() => setActiveTab('script-admin')}
            >
              <span className="sidebar-icon"><Phone size={18} /></span>
              <span>Script d'Appel</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'partner-keys' ? 'active' : ''}`}
              onClick={() => setActiveTab('partner-keys')}
            >
              <span className="sidebar-icon"><Key size={18} /></span>
              <span>Clés API Partenaires</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'fichier-pro' ? 'active' : ''}`}
              onClick={() => setActiveTab('fichier-pro')}
            >
              <span className="sidebar-icon"><Search size={18} /></span>
              <span>Configuration API Google</span>
            </button>
          </div>

          <div className="sidebar-section">
            <h4 className="sidebar-title">Communication</h4>
            <button
              className={`sidebar-item ${activeTab === 'notifications' ? 'active' : ''}`}
              onClick={() => setActiveTab('notifications')}
            >
              <span className="sidebar-icon"><Megaphone size={18} /></span>
              <span>Notifications</span>
            </button>
            <button
              className={`sidebar-item ${activeTab === 'emails' ? 'active' : ''}`}
              onClick={() => setActiveTab('emails')}
            >
              <span className="sidebar-icon"><Mail size={18} /></span>
              <span>Emails</span>
            </button>
          </div>
        </aside>
      )}

      {/* Tabs pour les agents, chefs d'équipe et autres utilisateurs non-admin */}
      {activeTab !== 'simulateur' && currentUser && currentUser.role !== 'admin' && (currentUser.role !== 'client' || currentUser.has_crm_access === 1) && (
        <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 6px', maxWidth: 360 }}>
          <input
            type="search"
            value={rechercheOnglet}
            onChange={(e) => setRechercheOnglet(e.target.value)}
            placeholder="Rechercher un onglet…"
            aria-label="Rechercher un onglet"
            style={{ flex: 1, padding: '7px 12px', border: '1px solid #d5d5e0', borderRadius: 5,
                     fontSize: 13, outlineColor: '#0d47a1' }}
          />
          {rechercheOnglet && (
            <button type="button" onClick={() => setRechercheOnglet('')}
                    style={{ border: '1px solid #d5d5e0', background: '#fff', borderRadius: 5,
                             padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}>
              Effacer
            </button>
          )}
        </div>
        <div className="tabs" ref={barreOngletsRef}>
          {/* Valideur : ces deux onglets, et rien d'autre */}
          {currentUser.role === 'valideur' && (
            <>
              <button
                className={`tab ${activeTab === 'compte-assu' ? 'active' : ''}`}
                onClick={() => setActiveTab('compte-assu')}
              >
                <WalletIcon size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Compte Assu
              </button>
              <button
                className={`tab ${activeTab === 'candidats-assu' ? 'active' : ''}`}
                onClick={() => setActiveTab('candidats-assu')}
              >
                <GraduationCap size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Candidat Assu
              </button>
            </>
          )}
          {/* Candidat validé : son espace, et rien d'autre */}
          {currentUser.role === 'candidat_assu' && (
            <button
              className={`tab ${activeTab === 'mon-espace' ? 'active' : ''}`}
              onClick={() => setActiveTab('mon-espace')}
            >
              <User size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Mon espace
            </button>
          )}
          {/* Suivi E-SHOPS CONNECT : l'admin, et le role suivi_connect qui n'a que lui */}
          {(currentUser.role === 'suivi_connect' || (currentUser.role === 'admin' && !tabHidden('suivi-connect'))) && (
            <button
              className={`tab ${activeTab === 'suivi-connect' ? 'active' : ''}`}
              onClick={() => setActiveTab('suivi-connect')}
            >
              <Globe size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Suivi E-SHOPS CONNECT
            </button>
          )}
          {/* ia.e-application.fr : l'admin, et le rôle commercial_ia qui n'a que lui */}
          {(currentUser.role === 'commercial_ia' || (currentUser.role === 'admin' && !tabHidden('prospection-ia'))) && (
            <button
              className={`tab ${activeTab === 'prospection-ia' ? 'active' : ''}`}
              onClick={() => setActiveTab('prospection-ia')}
            >
              <Search size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Prospection ia
            </button>
          )}
          {(currentUser.role === 'commercial_ia' || (currentUser.role === 'admin' && !tabHidden('ia-clients'))) && (
            <button
              className={`tab ${activeTab === 'ia-clients' ? 'active' : ''}`}
              onClick={() => setActiveTab('ia-clients')}
            >
              <UsersRound size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Clients IA Shops
            </button>
          )}
          {/* RH : un seul onglet, et rien d'autre */}
          {currentUser.role === 'rh' && (
            <button
              className={`tab ${activeTab === 'candidats-assu' ? 'active' : ''}`}
              onClick={() => setActiveTab('candidats-assu')}
            >
              <GraduationCap size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Candidat Assu
            </button>
          )}
          {false && (currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin') && (
            <button
              className={`tab ${activeTab === 'fichier-pro' ? 'active' : ''}`}
              onClick={() => setActiveTab('fichier-pro')}
            >
              <Search size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Recherche Pro
            </button>
          )}
          {(currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin' || currentUser.role === 'commercial') && currentUser.pole === 'eshops' && !tabHidden('eshops-prosper') && (
            <button
              className={`tab ${activeTab === 'eshops-prosper' ? 'active' : ''}`}
              onClick={() => setActiveTab('eshops-prosper')}
            >
              <Search size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Recherche Pro
            </button>
          )}
          {currentUser.pole === 'cee' && !tabHidden('prospect-cee') && (
            <button
              className={`tab ${activeTab === 'prospect-cee' ? 'active' : ''}`}
              onClick={() => setActiveTab('prospect-cee')}
            >
              <Search size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Prospect CEE
            </button>
          )}
          {(currentUser.pole === 'cee') && !tabHidden('leads-cee') && (
            <button
              className={`tab ${activeTab === 'leads-cee' ? 'active' : ''}`}
              onClick={() => setActiveTab('leads-cee')}
            >
              <Target size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Leads CEE
            </button>
          )}
          {(currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin' || currentUser.role === 'commercial') && currentUser.pole === 'eshops' && !tabHidden('base-perso') && (
            <button
              className={`tab ${activeTab === 'base-perso' ? 'active' : ''}`}
              onClick={() => setActiveTab('base-perso')}
            >
              <FolderOpen size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Importer Base
            </button>
          )}
          {(currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin') && currentUser.has_extraction_access === 1 && !tabHidden('prospects-extraction') && (
            <button
              className={`tab ${activeTab === 'prospects-extraction' ? 'active' : ''}`}
              onClick={() => setActiveTab('prospects-extraction')}
            >
              <Upload size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Prospects Extraction
            </button>
          )}
          {(currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin') && currentUser.pole === 'eshops' && !tabHidden('planning') && (
            <button
              className={`tab ${activeTab === 'planning' ? 'active' : ''}`}
              onClick={() => setActiveTab('planning')}
            >
              <Calendar size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Planning
            </button>
          )}
          {currentUser.pole === 'cee' && !tabHidden('planning-cee') && (
            <button
              className={`tab ${activeTab === 'planning-cee' ? 'active' : ''}`}
              onClick={() => setActiveTab('planning-cee')}
            >
              <Calendar size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Planning CEE
            </button>
          )}
          {(currentUser.role === 'agent' && currentUser.id === 2) && (
            <button
              className={`tab ${activeTab === 'leads' ? 'active' : ''}`}
              onClick={() => setActiveTab('leads')}
            >
              <Target size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Leads
            </button>
          )}
          {(currentUser.role === 'admin' || currentUser.role === 'sous_admin') && !tabHidden('clients-monetico') && (
            <button
              className={`tab ${activeTab === 'clients-monetico' ? 'active' : ''}`}
              onClick={() => setActiveTab('clients-monetico')}
            >
              <CreditCard size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Clients Monetico
            </button>
          )}
          {(currentUser.role === 'admin' || currentUser.role === 'sous_admin') && !tabHidden('emandat') && (
            <button
              className={`tab ${activeTab === 'emandat' ? 'active' : ''}`}
              onClick={() => setActiveTab('emandat')}
            >
              <Shield size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Dossiers E-Mandat
            </button>
          )}
          {(currentUser.role === 'admin' || currentUser.role === 'sous_admin') && !tabHidden('data-esprotect') && (
            <button
              className={`tab ${activeTab === 'data-esprotect' ? 'active' : ''}`}
              onClick={() => setActiveTab('data-esprotect')}
            >
              <Database size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Data ES-PROTECT
            </button>
          )}
          {(currentUser.role === 'admin' || currentUser.role === 'sous_admin') && !tabHidden('mail-esprotect') && (
            <button
              className={`tab ${activeTab === 'mail-esprotect' ? 'active' : ''}`}
              onClick={() => setActiveTab('mail-esprotect')}
            >
              <Mail size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Mail ES-PROTECT
            </button>
          )}
          {(currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin') && currentUser.pole === 'eshops' && !tabHidden('script-appel') && (
            <button
              className={`tab ${activeTab === 'script-appel' ? 'active' : ''}`}
              onClick={() => setActiveTab('script-appel')}
            >
              <Phone size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Script d'Appel
            </button>
          )}
          {currentUser.pole === 'cee' && !tabHidden('script-cee') && (
            <button
              className={`tab ${activeTab === 'script-cee' ? 'active' : ''}`}
              onClick={() => setActiveTab('script-cee')}
            >
              <Phone size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Script CEE
            </button>
          )}
          {currentUser.pole === 'pacpv' && !tabHidden('prospect-pacpv') && (
            <button className={`tab ${activeTab === 'prospect-pacpv' ? 'active' : ''}`} onClick={() => setActiveTab('prospect-pacpv')}>
              <Search size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Prospect PAC/PV
            </button>
          )}
          {currentUser.pole === 'pacpv' && !tabHidden('leads-pacpv') && (
            <button className={`tab ${activeTab === 'leads-pacpv' ? 'active' : ''}`} onClick={() => setActiveTab('leads-pacpv')}>
              <Target size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Leads PAC/PV
            </button>
          )}
          {currentUser.pole === 'pacpv' && !tabHidden('planning-pacpv') && (
            <button className={`tab ${activeTab === 'planning-pacpv' ? 'active' : ''}`} onClick={() => setActiveTab('planning-pacpv')}>
              <Calendar size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Planning PAC/PV
            </button>
          )}
          {currentUser.pole === 'pacpv' && !tabHidden('script-pacpv') && (
            <button className={`tab ${activeTab === 'script-pacpv' ? 'active' : ''}`} onClick={() => setActiveTab('script-pacpv')}>
              <Phone size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Script PAC/PV
            </button>
          )}
          {(currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin') && (
            <button
              className={`tab ${activeTab === 'mon-equipe' ? 'active' : ''}`}
              onClick={() => setActiveTab('mon-equipe')}
            >
              <UsersRound size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Mon Équipe
            </button>
          )}
          {(currentUser.role === 'commercial' && (currentUser.id === 48 || currentUser.id === 112)) && (
            <button
              className={`tab ${activeTab === 'planning' ? 'active' : ''}`}
              onClick={() => setActiveTab('planning')}
            >
              <Calendar size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Planning
            </button>
          )}
          {((currentUser.role === 'agent' && (currentUser.id === 65 || currentUser.id === 68)) || (currentUser.role === 'commercial' && currentUser.id === 48)) && (
            <button
              className={`tab ${activeTab === 'emails' ? 'active' : ''}`}
              onClick={() => setActiveTab('emails')}
            >
              <Mail size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Emails
            </button>
          )}
          {!['sous_admin', 'valideur', 'rh', 'candidat_assu', 'suivi_connect', 'commercial_ia'].includes(currentUser.role) && !tabHidden('clients') && (
            <button
              className={`tab ${activeTab === 'clients' ? 'active' : ''}`}
              onClick={() => setActiveTab('clients')}
            >
              {currentUser.role === 'client' && currentUser.has_crm_access === 1 ? 'Gestion de mes clients' : 'Gestion des Clients'}
            </button>
          )}
          {currentUser.role === 'client' && currentUser.has_crm_access === 1 && (
            <>
              <button
                className={`tab ${activeTab === 'stripe-payments' ? 'active' : ''}`}
                onClick={() => setActiveTab('stripe-payments')}
              >
                Mes Paiements Stripe
              </button>
              <button
                className={`tab ${activeTab === 'mon-compte' ? 'active' : ''}`}
                onClick={() => setActiveTab('mon-compte')}
              >
                Mon Compte E-Shop
              </button>
            </>
          )}
          {(currentUser.role === 'agent' || currentUser.role === 'commercial') && !tabHidden('compte') && (
            <button
              className={`tab ${activeTab === 'compte' ? 'active' : ''}`}
              onClick={() => setActiveTab('compte')}
            >
              Gestion compte
            </button>
          )}
          {currentUser.role === 'commercial' && (
            <button
              className={`tab ${activeTab === 'simulateur' ? 'active' : ''}`}
              onClick={() => setActiveTab('simulateur')}
            >
              <Calculator size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Simulateur
            </button>
          )}
          {currentUser.role === 'commercial' && (
            <button
              className={`tab ${activeTab === 'partner-keys' ? 'active' : ''}`}
              onClick={() => setActiveTab('partner-keys')}
            >
              <Key size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Clés API
            </button>
          )}
        </div>
        </>
      )}

      {activeTab === 'mon-compte' && currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1 ? (
        // Onglet Mon Compte : Compteurs + Informations personnelles
        <main className="dashboard-main">
          {/* Compteurs pour l'onglet Mon Compte */}
          <div className="stats-cards">
            <div className="stat-card count">
              <h3>Mes Clients</h3>
              <p className="stat-number">{clients.length}</p>
            </div>
            <div className="stat-card count">
              <h3>Clients Payés</h3>
              <p className="stat-number">{clients.filter(c => c.paye).length}</p>
            </div>
            <div className="stat-card money">
              <h3><Coins size={20} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Chiffre d'Affaires</h3>
              <p className="stat-number">{stats && stats.ca_total ? stats.ca_total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '0,00 €'}</p>
            </div>
          </div>

          {/* Section Informations Personnelles - Affichage automatique */}
          {showDetail && viewingClient ? (
            <ClientDetail
              client={viewingClient}
              onClose={() => {
                setShowDetail(false);
                setViewingClient(null);
              }}
              isClientView={true}
            />
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', background: 'white', borderRadius: '15px', marginTop: '30px' }}>
              <h3 style={{ color: '#667eea', fontSize: '20px', marginBottom: '20px' }}>Mes Informations Personnelles</h3>
              <p style={{ color: '#666' }}>Chargement de vos informations...</p>
            </div>
          )}
        </main>
      ) : activeTab === 'stripe-payments' && currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1 ? (
        // Onglet Mes Paiements Stripe
        <main className="dashboard-main">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
            <h2 className="section-title" style={{ color: 'white', margin: 0 }}>Mes Paiements Stripe</h2>
            <button
              onClick={() => setShowStripeConfigModal(true)}
              style={{
                background: stripeConfig?.has_stripe_config ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600'
              }}
            >
              {stripeConfig?.has_stripe_config ? <><Settings size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Modifier clé Stripe</> : <><Key size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Configurer Stripe</>}
            </button>
          </div>

          {stripeConfig && !stripeConfig.has_stripe_config ? (
            <div style={{ background: 'white', padding: '40px', borderRadius: '15px', textAlign: 'center' }}>
              <h3 style={{ color: '#667eea', fontSize: '24px', marginBottom: '20px' }}><Download size={20} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Connectez votre compte Stripe</h3>
              <p style={{ color: '#666', marginBottom: '30px', fontSize: '16px' }}>
                Pour suivre vos paiements Stripe, vous devez configurer votre clé API Stripe Secret.
              </p>
              <button
                onClick={() => setShowStripeConfigModal(true)}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '15px 40px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: '600'
                }}
              >
                Configurer maintenant
              </button>
            </div>
          ) : loadingEshopStripePayments ? (
            <div style={{ background: 'white', padding: '40px', borderRadius: '15px', textAlign: 'center' }}>
              <p style={{ color: '#666' }}>Chargement des paiements Stripe...</p>
            </div>
          ) : eshopStripePayments.length === 0 ? (
            <div style={{ background: 'white', padding: '40px', borderRadius: '15px', textAlign: 'center' }}>
              <p style={{ color: '#666' }}>Aucun paiement Stripe trouvé</p>
            </div>
          ) : (
            <div style={{ background: 'white', padding: '30px', borderRadius: '15px' }}>
              <h3 style={{ color: '#667eea', fontSize: '20px', marginBottom: '20px' }}>
                Historique des Paiements ({eshopStripePayments.length})
              </h3>
              <div className="clients-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>ID Transaction</th>
                      <th>Description</th>
                      <th>Email Client</th>
                      <th>Montant</th>
                      <th>Statut</th>
                      <th>Reçu</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eshopStripePayments.map((payment) => (
                      <tr key={payment.id}>
                        <td>{new Date(payment.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                        <td><strong>{payment.id.substring(0, 20)}...</strong></td>
                        <td>{payment.description}</td>
                        <td>{payment.customer_email}</td>
                        <td><strong>{payment.amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {payment.currency.toUpperCase()}</strong></td>
                        <td>
                          <span className={`status-badge ${payment.status === 'succeeded' ? 'status-yes' : 'status-no'}`}>
                            {payment.status === 'succeeded' ? '✓ Payé' : '✗ Échoué'}
                          </span>
                        </td>
                        <td>
                          {payment.receipt_url && (
                            <a href={payment.receipt_url} target="_blank" rel="noopener noreferrer" className="btn-view" style={{ fontSize: '12px' }}>
                              Voir reçu
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Modal de configuration Stripe */}
          {showStripeConfigModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: 'white',
                padding: '40px',
                borderRadius: '15px',
                maxWidth: '600px',
                width: '90%'
              }}>
                <h3 style={{ color: '#667eea', fontSize: '24px', marginBottom: '20px' }}>Configuration Stripe</h3>

                {stripeConfig?.masked_key && (
                  <div style={{ marginBottom: '20px', padding: '15px', background: '#f0f4ff', borderRadius: '8px' }}>
                    <p style={{ margin: 0, color: '#666' }}>
                      <strong>Clé actuelle :</strong> {stripeConfig.masked_key}
                    </p>
                  </div>
                )}

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#333' }}>
                    Clé API Stripe Secret (commence par sk_)
                  </label>
                  <input
                    type="password"
                    value={stripeApiKey}
                    onChange={(e) => setStripeApiKey(e.target.value)}
                    placeholder="sk_test_... ou sk_live_..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: '2px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '16px'
                    }}
                  />
                  <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                    <Lightbulb size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Trouvez votre clé API dans votre{' '}
                    <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" style={{ color: '#667eea' }}>
                      tableau de bord Stripe
                    </a>
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => {
                      setShowStripeConfigModal(false);
                      setStripeApiKey('');
                    }}
                    style={{
                      background: '#ddd',
                      color: '#333',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSaveStripeConfig}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      ) : activeTab === 'users' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <UserManagement />
        </main>
      ) : activeTab === 'documents' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <DocumentsManagement />
        </main>
      ) : activeTab === 'devis' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <DevisManagement
            user={currentUser}
            token={localStorage.getItem('token')}
            onBack={() => setActiveTab('clients')}
          />
        </main>
      ) : activeTab === 'factures' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <FacturesManagement
            user={currentUser}
            token={localStorage.getItem('token')}
            onBack={() => setActiveTab('clients')}
          />
        </main>
      ) : activeTab === 'prestations' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <PrestationsManagement
            token={localStorage.getItem('token')}
            onBack={() => setActiveTab('devis')}
          />
        </main>
      ) : activeTab === 'influenceurs' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <InfluenceursManagement token={localStorage.getItem('token')} />
        </main>
      ) : activeTab === 'notifications' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <NotificationCenter />
        </main>
      ) : activeTab === 'emails' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <EmailCenter />
        </main>
      ) : activeTab === 'emails' && currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1 ? (
        <main className="dashboard-main">
          <EmailCenter isEshopOwner={true} />
        </main>
      ) : activeTab === 'emails' && currentUser && currentUser.role === 'agent' && (currentUser.id === 65 || currentUser.id === 68) ? (
        <main className="dashboard-main">
          <EmailCenter manualOnly={true} />
        </main>
      ) : activeTab === 'emails' && currentUser && currentUser.role === 'commercial' && currentUser.id === 48 ? (
        <main className="dashboard-main">
          <EmailCenter isCommercial={true} />
        </main>
      ) : activeTab === 'script-admin' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <ScriptAppelAdmin />
        </main>
      ) : activeTab === 'cahier-charges' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <CahierDesCharges currentUser={currentUser} />
        </main>
      ) : activeTab === 'leads' && currentUser && (currentUser.role === 'admin' || currentUser.role === 'agent') ? (
        <main className="dashboard-main">
          <LeadsManagement currentUser={currentUser} />
        </main>
      ) : activeTab === 'mon-espace' && currentUser && currentUser.role === 'candidat_assu' ? (
        <EspaceCandidat currentUser={currentUser} />
      ) : activeTab === 'candidats-assu' && currentUser && ['admin', 'valideur', 'rh'].includes(currentUser.role) ? (
        <main className="dashboard-main">
          <CandidatsAssu currentUser={currentUser} />
        </main>
      ) : activeTab === 'compte-assu' && currentUser && ['admin', 'valideur'].includes(currentUser.role) ? (
        <main className="dashboard-main">
          <CompteAssu currentUser={currentUser} />
        </main>
      ) : activeTab === 'fichier-pro' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <FichierPro currentUser={currentUser} />
        </main>
      ) : activeTab === 'eshops-prosper' && currentUser && currentUser.pole === 'eshops' && (currentUser.role === 'admin' || currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin' || currentUser.role === 'commercial') ? (
        <main className="dashboard-main">
          {/* Recherche Pro v2 (08/10/2026) : cartes, filtres, Encaisser. L'ancien ecran : EShopsProsper.js */}
          <RecherchePro currentUser={currentUser} />
        </main>
      ) : activeTab === 'prospect-cee' && currentUser && (currentUser.role === 'admin' || currentUser.pole === 'cee') ? (
        <main className="dashboard-main">
          <ProspectCEE currentUser={currentUser} />
        </main>
      ) : activeTab === 'leads-cee' && currentUser && (currentUser.pole === 'cee') ? (
        <main className="dashboard-main">
          <LeadsCEEAgent currentUser={currentUser} />
        </main>
      ) : activeTab === 'prospect-pacpv' && currentUser && (currentUser.role === 'admin' || currentUser.pole === 'pacpv') ? (
        <main className="dashboard-main">
          <ProspectCEE currentUser={currentUser} pole="pacpv" />
        </main>
      ) : activeTab === 'leads-pacpv' && currentUser && (currentUser.role === 'admin' || currentUser.pole === 'pacpv') ? (
        <main className="dashboard-main">
          <LeadsCEEAgent currentUser={currentUser} leadType="pacpv" />
        </main>
      ) : activeTab === 'planning-pacpv' && currentUser && (currentUser.role === 'admin' || currentUser.pole === 'pacpv') ? (
        <main className="dashboard-main">
          <Planning userRole={currentUser.role} userId={currentUser.id} planningType="pacpv" />
        </main>
      ) : activeTab === 'script-pacpv' && currentUser && (currentUser.role === 'admin' || currentUser.pole === 'pacpv') ? (
        <main className="dashboard-main">
          <ScriptAppel scriptType="pacpv" />
        </main>
      ) : activeTab === 'data-pacpv' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <DataPacPv />
        </main>
      ) : activeTab === 'leads-cee-admin' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <LeadsCEEAdmin />
        </main>
      ) : activeTab === 'compagne-cee' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <CompagneCEE />
        </main>
      ) : activeTab === 'base-perso' && currentUser && currentUser.pole === 'eshops' && (currentUser.role === 'admin' || currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin' || currentUser.role === 'commercial') ? (
        <main className="dashboard-main">
          <BasePerso currentUser={currentUser} />
        </main>
      ) : activeTab === 'prospects' && currentUser && (currentUser.role === 'admin' || currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin') ? (
        <main className="dashboard-main">
          <DataPro currentUser={currentUser} />
        </main>
      ) : activeTab === 'prospects-extraction' && currentUser && (currentUser.role === 'admin' || ((currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin') && currentUser.has_extraction_access === 1)) ? (
        <main className="dashboard-main">
          <ProspectManagement userRole={currentUser.role} type="extraction" />
        </main>
      ) : activeTab === 'partner-keys' && currentUser && (currentUser.role === 'admin' || currentUser.role === 'commercial') ? (
        <PartnerApiKeys currentUser={currentUser} />
      ) : activeTab === 'simulateur' && currentUser && (currentUser.role === 'admin' || currentUser.role === 'commercial') ? (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, background: '#ffffff', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '10px 15px', flexShrink: 0 }}>
            <button
              onClick={() => setActiveTab('eshops-prosper')}
              style={{
                padding: '8px 16px', borderRadius: '8px', border: 'none',
                background: '#667eea', color: '#fff', fontWeight: '600',
                cursor: 'pointer', fontSize: '13px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
            >
              ← Retour
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <Simulateur user={currentUser} token={localStorage.getItem('token')} />
          </div>
        </div>
      ) : activeTab === 'planning' && currentUser && currentUser.pole === 'eshops' && (currentUser.role === 'admin' || currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin') ? (
        <main className="dashboard-main">
          <Planning userRole={currentUser.role} userId={currentUser.id} />
        </main>
      ) : activeTab === 'planning-cee' && currentUser && (currentUser.role === 'admin' || currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin') ? (
        <main className="dashboard-main">
          <Planning userRole={currentUser.role} userId={currentUser.id} planningType="cee" />
        </main>
      ) : activeTab === 'planning' && currentUser && currentUser.role === 'commercial' && currentUser.id === 48 ? (
        <main className="dashboard-main">
          <Planning userRole="agent" userId={currentUser.id} allowedCommercialIds={[1, 3]} />
        </main>
      ) : activeTab === 'planning' && currentUser && currentUser.role === 'commercial' && currentUser.id === 112 ? (
        <main className="dashboard-main">
          <Planning userRole="agent" userId={currentUser.id} allowedCommercialIds={[4]} onlyConfirmed={true} />
        </main>
      ) : activeTab === 'clients-monetico' && currentUser && (currentUser.role === 'admin' || currentUser.role === 'sous_admin') ? (
        <main className="dashboard-main">
          <ClientsMonetico />
        </main>
      ) : activeTab === 'emandat' && currentUser && (currentUser.role === 'admin' || currentUser.role === 'sous_admin') ? (
        <main className="dashboard-main">
          <EmandatClients />
        </main>
      ) : activeTab === 'suivi-connect' && currentUser && ['admin', 'suivi_connect'].includes(currentUser.role) ? (
        <main className="dashboard-main">
          <SuiviConnect />
        </main>
      ) : activeTab === 'ia-clients' && currentUser && ['admin', 'commercial_ia'].includes(currentUser.role) ? (
        <main className="dashboard-main">
          <IaClients currentUser={currentUser} />
        </main>
      ) : activeTab === 'prospection-ia' && currentUser && ['admin', 'sous_admin', 'commercial_ia'].includes(currentUser.role) ? (
        <main className="dashboard-main">
          <ProspectionIa currentUser={currentUser} />
        </main>
      ) : activeTab === 'data-esprotect' && currentUser && (currentUser.role === 'admin' || currentUser.role === 'sous_admin') ? (
        <main className="dashboard-main">
          <DataEsprotect />
        </main>
      ) : activeTab === 'mail-esprotect' && currentUser && (currentUser.role === 'admin' || currentUser.role === 'sous_admin') ? (
        <main className="dashboard-main">
          <MailEsprotect />
        </main>
      ) : activeTab === 'boite-mail' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <BoiteMail />
        </main>
      ) : activeTab === 'mon-assistant' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <AdminAssistant />
        </main>
      ) : activeTab === 'script-appel' && currentUser && currentUser.pole === 'eshops' && (currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin') ? (
        <main className="dashboard-main">
          <ScriptAppel scriptType="standard" />
        </main>
      ) : activeTab === 'script-cee' && currentUser && (currentUser.role === 'admin' || currentUser.pole === 'cee') ? (
        <main className="dashboard-main">
          <ScriptAppel scriptType="cee" />
        </main>
      ) : activeTab === 'mon-equipe' && currentUser && (currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin') ? (
        <main className="dashboard-main">
          <TeamManagement currentUser={currentUser} />
        </main>
      ) : activeTab === 'comptes' && currentUser && currentUser.role === 'admin' ? (
        <main className="dashboard-main">
          <h2 className="section-title" style={{ color: 'white', marginBottom: '30px' }}>Gestion des Comptes</h2>

          {/* Section Stats Équipe */}
          {financialStats && financialStats.detail_users && (
            <section style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: '20px',
              padding: '30px',
              marginBottom: '30px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }}>
              <h3 style={{ color: 'white', fontSize: '22px', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <BarChart3 size={28} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Statistiques Équipe
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '20px' }}>
                {/* Nombre d'agents */}
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '15px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: '800', color: 'white' }}>
                    {financialStats.detail_users.filter(u => u.role === 'agent' || u.role === 'superviseur_agent').length}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginTop: '5px' }}>Agents</div>
                </div>

                {/* Nombre de commerciaux */}
                <div style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  borderRadius: '15px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: '800', color: 'white' }}>
                    {financialStats.detail_users.filter(u => u.role === 'commercial').length}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginTop: '5px' }}>Commerciaux</div>
                </div>

                {/* Chefs d'équipe */}
                <div style={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  borderRadius: '15px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: '800', color: 'white' }}>
                    {financialStats.detail_users.filter(u => u.role === 'chef_equipe').length}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginTop: '5px' }}>Chefs d'équipe</div>
                </div>

                {/* Total commissions dues */}
                <div style={{
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  borderRadius: '15px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: '800', color: 'white' }}>
                    {(financialStats.commissions_payees + financialStats.commissions_a_venir).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginTop: '5px' }}>Total Commissions</div>
                </div>

                {/* Taux de paiement commissions */}
                <div style={{
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  borderRadius: '15px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '36px', fontWeight: '800', color: 'white' }}>
                    {((financialStats.commissions_payees / (financialStats.commissions_payees + financialStats.commissions_a_venir || 1)) * 100).toFixed(0)}%
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginTop: '5px' }}>Com. Payées</div>
                </div>

                {/* Moyenne commission par agent */}
                <div style={{
                  background: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
                  borderRadius: '15px',
                  padding: '20px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '28px', fontWeight: '800', color: 'white' }}>
                    {(financialStats.detail_users.reduce((sum, u) => sum + (u.commissions_totales || 0), 0) / (financialStats.detail_users.length || 1)).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', marginTop: '5px' }}>Moy. / Personne</div>
                </div>
              </div>

              {/* Top performers par catégorie */}
              {financialStats.detail_users.length > 0 && (
                <div style={{ marginTop: '25px' }}>
                  <h4 style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', marginBottom: '15px' }}><Gift size={16} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Top Performers</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>

                    {/* Top Agent */}
                    {(() => {
                      const topAgent = financialStats.detail_users
                        .filter(u => u.role === 'agent' || u.role === 'superviseur_agent')
                        .sort((a, b) => (b.commissions_totales || 0) - (a.commissions_totales || 0))[0];
                      return topAgent ? (
                        <div style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '15px',
                          padding: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '15px'
                        }}>
                          <div style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '28px'
                          }}>
                            🥇
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', textTransform: 'uppercase', fontWeight: '600' }}>Top Agent</div>
                            <div style={{ color: 'white', fontWeight: '700', fontSize: '18px', marginTop: '4px' }}>{topAgent.nom}</div>
                            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', fontWeight: '600', marginTop: '4px' }}>
                              {(topAgent.commissions_totales || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0 })} €
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          background: 'rgba(255,255,255,0.1)',
                          borderRadius: '15px',
                          padding: '20px',
                          textAlign: 'center',
                          color: 'rgba(255,255,255,0.5)'
                        }}>
                          Aucun agent
                        </div>
                      );
                    })()}

                    {/* Top Commercial */}
                    {(() => {
                      const topCommercial = financialStats.detail_users
                        .filter(u => u.role === 'commercial')
                        .sort((a, b) => (b.commissions_totales || 0) - (a.commissions_totales || 0))[0];
                      return topCommercial ? (
                        <div style={{
                          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          borderRadius: '15px',
                          padding: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '15px'
                        }}>
                          <div style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '28px'
                          }}>
                            🥇
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', textTransform: 'uppercase', fontWeight: '600' }}>Top Commercial</div>
                            <div style={{ color: 'white', fontWeight: '700', fontSize: '18px', marginTop: '4px' }}>{topCommercial.nom}</div>
                            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', fontWeight: '600', marginTop: '4px' }}>
                              {(topCommercial.commissions_totales || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0 })} €
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          background: 'rgba(255,255,255,0.1)',
                          borderRadius: '15px',
                          padding: '20px',
                          textAlign: 'center',
                          color: 'rgba(255,255,255,0.5)'
                        }}>
                          Aucun commercial
                        </div>
                      );
                    })()}

                    {/* Top Chef d'équipe */}
                    {(() => {
                      const topChef = financialStats.detail_users
                        .filter(u => u.role === 'chef_equipe')
                        .sort((a, b) => (b.commissions_totales || 0) - (a.commissions_totales || 0))[0];
                      return topChef ? (
                        <div style={{
                          background: 'linear-gradient(135deg, #f5af19 0%, #f12711 100%)',
                          borderRadius: '15px',
                          padding: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '15px'
                        }}>
                          <div style={{
                            width: '60px',
                            height: '60px',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '28px'
                          }}>
                            🥇
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', textTransform: 'uppercase', fontWeight: '600' }}>Top Chef d'équipe</div>
                            <div style={{ color: 'white', fontWeight: '700', fontSize: '18px', marginTop: '4px' }}>{topChef.nom}</div>
                            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', fontWeight: '600', marginTop: '4px' }}>
                              {(topChef.commissions_totales || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0 })} €
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div style={{
                          background: 'rgba(255,255,255,0.1)',
                          borderRadius: '15px',
                          padding: '20px',
                          textAlign: 'center',
                          color: 'rgba(255,255,255,0.5)'
                        }}>
                          Aucun chef d'équipe
                        </div>
                      );
                    })()}

                  </div>
                </div>
              )}
            </section>
          )}

          {financialStats ? (
            <>
              {/* Ligne 1: Bons de Commande, Commissions Payées, Reste à Encaisser */}
              <div className="stats-cards" style={{ marginBottom: '25px' }}>
                <div className="stat-card" style={{ background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)' }}>
                  <h3 style={{ color: 'white' }}>Bons de Commande</h3>
                  <p className="stat-number" style={{ color: 'white' }}>{financialStats.total_bons_commande.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                </div>
                <div className="stat-card" style={{ background: 'linear-gradient(135deg, #a29bfe 0%, #6c5ce7 100%)' }}>
                  <h3 style={{ color: 'white' }}>Commissions Payées</h3>
                  <p className="stat-number" style={{ color: 'white' }}>{financialStats.commissions_payees.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                </div>
                <div className="stat-card" style={{ background: 'linear-gradient(135deg, #fd79a8 0%, #e84393 100%)' }}>
                  <h3 style={{ color: 'white' }}>Reste à Encaisser</h3>
                  <p className="stat-number" style={{ color: 'white' }}>{(financialStats.total_bons_commande - financialStats.ca_paye).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                </div>
              </div>

              {/* Ligne 2: CA Réel, Commissions à Payer, Bénéfice */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '20px',
                marginBottom: '30px'
              }}>
                {/* CA Réel */}
                <div style={{
                  background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                  borderRadius: '20px',
                  padding: '30px',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '16px', opacity: 0.9, marginBottom: '10px' }}>CA Réel Encaissé</div>
                  <div style={{ fontSize: '36px', fontWeight: '800' }}>
                    {financialStats.ca_paye.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '8px' }}>
                    {clients.filter(c => c.paye).length} clients payés
                  </div>
                </div>

                {/* Commissions à Payer */}
                <div style={{
                  background: 'linear-gradient(135deg, #e67e22 0%, #f39c12 100%)',
                  borderRadius: '20px',
                  padding: '30px',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '16px', opacity: 0.9, marginBottom: '10px' }}>Commissions à Payer</div>
                  <div style={{ fontSize: '36px', fontWeight: '800' }}>
                    {financialStats.commissions_a_venir.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '8px' }}>
                    Reste à verser
                  </div>
                </div>

                {/* Bénéfice */}
                <div style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '20px',
                  padding: '30px',
                  color: 'white',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '16px', opacity: 0.9, marginBottom: '10px' }}>Bénéfice Net</div>
                  <div style={{ fontSize: '36px', fontWeight: '800' }}>
                    {(financialStats.ca_paye - financialStats.commissions_payees - financialStats.commissions_a_venir).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                  </div>
                  <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '8px' }}>
                    CA - Toutes commissions
                  </div>
                </div>
              </div>

              {/* Détail calcul */}
              <div style={{
                background: 'white',
                borderRadius: '15px',
                padding: '25px',
                marginBottom: '30px'
              }}>
                <h3 style={{ color: '#333', fontSize: '16px', marginBottom: '20px' }}>Détail du calcul</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ color: '#666' }}>CA Réel Encaissé</span>
                    <span style={{ fontWeight: '600', color: '#27ae60' }}>+{financialStats.ca_paye.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ color: '#666' }}>Commissions déjà payées</span>
                    <span style={{ fontWeight: '600', color: '#e74c3c' }}>-{financialStats.commissions_payees.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #eee' }}>
                    <span style={{ color: '#666' }}>Commissions à payer</span>
                    <span style={{ fontWeight: '600', color: '#f39c12' }}>-{financialStats.commissions_a_venir.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '15px', background: '#f8f9fa', borderRadius: '8px', marginTop: '10px' }}>
                    <span style={{ fontWeight: '700', color: '#333' }}>= Bénéfice Net</span>
                    <span style={{ fontWeight: '800', color: '#667eea', fontSize: '18px' }}>{(financialStats.ca_paye - financialStats.commissions_payees - financialStats.commissions_a_venir).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px' }}>Chargement des statistiques...</div>
          )}

          <section style={{ background: 'white', padding: '30px', borderRadius: '15px', marginTop: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
              <div>
                <h3 style={{ color: '#667eea', fontSize: '20px', margin: 0 }}>Fiches Agents / Commerciaux</h3>
                <p style={{ color: '#888', fontSize: '14px', margin: '5px 0 0 0' }}>Cliquez sur une fiche pour voir l'historique complet</p>
              </div>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Rechercher un agent..."
                  value={searchFiche}
                  onChange={(e) => setSearchFiche(e.target.value)}
                  style={{
                    padding: '12px 20px 12px 45px',
                    borderRadius: '25px',
                    border: '2px solid #e9ecef',
                    fontSize: '14px',
                    width: '250px',
                    outline: 'none',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e9ecef';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <span style={{
                  position: 'absolute',
                  left: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '18px',
                  color: '#888'
                }}>
                  <Search size={18} />
                </span>
              </div>
            </div>

            {financialStats && financialStats.detail_users && financialStats.detail_users.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
                {financialStats.detail_users
                  .filter(user => user.nom.toLowerCase().includes(searchFiche.toLowerCase()))
                  .map((user) => (
                  <div
                    key={user.id}
                    onClick={() => fetchUserFiche(user.id)}
                    style={{
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      borderRadius: '15px',
                      padding: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      border: '2px solid transparent',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.08)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.borderColor = '#667eea';
                      e.currentTarget.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.2)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.borderColor = 'transparent';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
                      <div>
                        <h4 style={{ margin: 0, fontSize: '18px', color: '#333' }}>{user.nom}</h4>
                        <span style={{
                          display: 'inline-block',
                          marginTop: '5px',
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: user.role === 'agent' ? '#e3f2fd' : user.role === 'chef_equipe' || user.role === 'sous_admin' ? '#fff3e0' : user.role === 'superviseur' || user.role === 'superviseur_agent' ? '#e8f5e9' : '#fce4ec',
                          color: user.role === 'agent' ? '#1976d2' : user.role === 'chef_equipe' || user.role === 'sous_admin' ? '#f57c00' : user.role === 'superviseur' || user.role === 'superviseur_agent' ? '#388e3c' : '#c2185b'
                        }}>
                          {user.role === 'agent' ? 'Agent' : user.role === 'chef_equipe' ? 'Chef Equipe' : user.role === 'sous_admin' ? 'Sous-Admin' : user.role === 'superviseur' || user.role === 'superviseur_agent' ? 'Superviseur' : 'Commercial'}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '12px', color: '#888' }}>Commission</div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#667eea' }}>
                          {parseFloat(user.taux_commission || 0) > 0 ? `${parseFloat(user.taux_commission).toFixed(2)}%` :
                           parseFloat(user.commission_fixe || 0) > 0 ? `${parseFloat(user.commission_fixe).toFixed(0)}€/client` : '-'}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div style={{ background: 'white', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>Payées</div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#2e7d32' }}>
                          {user.commissions_payees.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                        </div>
                      </div>
                      <div style={{ background: 'white', padding: '10px', borderRadius: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '11px', color: '#888', marginBottom: '3px' }}>A venir</div>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: user.commissions_a_venir < 0 ? '#d32f2f' : '#f57c00' }}>
                          {user.commissions_a_venir.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                        </div>
                      </div>
                    </div>

                    {(user.avances || 0) > 0 && (
                      <div style={{ marginTop: '10px', padding: '8px', background: '#f3e5f5', borderRadius: '8px', textAlign: 'center' }}>
                        <span style={{ fontSize: '12px', color: '#9c27b0' }}>Avances: <strong>{(user.avances || 0).toLocaleString('fr-FR')} €</strong></span>
                      </div>
                    )}

                    <div style={{ marginTop: '15px', textAlign: 'center' }}>
                      <span style={{ fontSize: '12px', color: '#667eea', fontWeight: '500' }}>Voir la fiche complète →</span>
                    </div>
                  </div>
                ))}
                {financialStats.detail_users.filter(user => user.nom.toLowerCase().includes(searchFiche.toLowerCase())).length === 0 && searchFiche && (
                  <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: '#999' }}>
                    <p style={{ fontSize: '16px', marginBottom: '10px' }}>Aucun agent trouvé pour "{searchFiche}"</p>
                    <button
                      onClick={() => setSearchFiche('')}
                      style={{
                        background: '#667eea',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Effacer la recherche
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Aucun utilisateur avec des commissions</div>
            )}
          </section>

          <section style={{ background: 'white', padding: '30px', borderRadius: '15px', marginTop: '30px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
              <div>
                <h3 style={{ color: '#667eea', fontSize: '20px', margin: 0 }}>Historique des Commissions</h3>
                <p style={{ color: '#888', fontSize: '14px', margin: '5px 0 0 0' }}>{commissionsHistory.length} commission(s) enregistrée(s)</p>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {commissionsHistory.length === 0 && (
                  <button
                    onClick={handleMigrateCommissions}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '600',
                      boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
                    }}
                  >
                    <Download size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Importer l'historique existant
                  </button>
                )}
              </div>
            </div>

            {loadingCommissions ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Chargement des commissions...</div>
            ) : commissionsHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                <p style={{ fontSize: '16px', marginBottom: '15px' }}>Aucune commission enregistrée</p>
                <p style={{ fontSize: '14px', color: '#aaa' }}>Cliquez sur "Importer l'historique existant" pour récupérer les commissions des clients déjà payés</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', maxHeight: '600px', overflowY: 'auto', paddingRight: '10px' }}>
                {commissionsHistory.map((commission) => (
                  <div
                    key={commission.id}
                    style={{
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                      borderRadius: '12px',
                      padding: '20px',
                      border: '1px solid #e9ecef',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                    }}
                  >
                    {/* En-tête avec nom client bien visible */}
                    <div style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      margin: '-20px -20px 20px -20px',
                      padding: '20px',
                      borderRadius: '12px 12px 0 0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '15px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                        <span style={{
                          background: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          padding: '8px 16px',
                          borderRadius: '25px',
                          fontSize: '14px',
                          fontWeight: '700'
                        }}>
                          #{commission.client_numero || '-'}
                        </span>
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '20px', color: 'white' }}>{commission.client_nom}</div>
                          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
                            {new Date(commission.date_paiement).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                          </div>
                        </div>
                      </div>
                      <div style={{
                        background: 'rgba(255,255,255,0.95)',
                        padding: '12px 20px',
                        borderRadius: '12px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', fontWeight: '600' }}>Montant payé</div>
                        <div style={{ fontSize: '22px', fontWeight: '800', color: '#27ae60' }}>
                          {parseFloat(commission.montant_paye).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                        </div>
                      </div>
                    </div>

                    {/* Ligne 2: Agent + Commercial */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                      {/* Bloc Agent */}
                      <div style={{ background: '#e3f2fd', borderRadius: '10px', padding: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontSize: '12px', color: '#1976d2', fontWeight: '600', textTransform: 'uppercase' }}>Agent</span>
                          {commission.agent_id && commission.commission_agent > 0 && (
                            <span style={{
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: commission.commission_agent_payee ? '#c8e6c9' : '#ffecb3',
                              color: commission.commission_agent_payee ? '#2e7d32' : '#f57c00'
                            }}>
                              {commission.commission_agent_payee ? '✓ Payée' : 'En attente'}
                            </span>
                          )}
                        </div>
                        <div style={{ fontWeight: '600', fontSize: '15px', color: '#333', marginBottom: '5px' }}>
                          {commission.agent_nom || <span style={{ color: '#999', fontStyle: 'italic' }}>Non assigné</span>}
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: '#1976d2' }}>
                          {parseFloat(commission.commission_agent || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                        </div>
                        {currentUser && currentUser.role === 'admin' && commission.agent_id && commission.commission_agent > 0 && !commission.commission_agent_payee && (
                          <button
                            onClick={() => handleMarkCommissionPaidAgent(commission.id)}
                            style={{
                              marginTop: '10px',
                              background: '#1976d2',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              width: '100%'
                            }}
                          >
                            ✓ Marquer payée
                          </button>
                        )}
                      </div>

                      {/* Bloc Commercial */}
                      <div style={{ background: '#fce4ec', borderRadius: '10px', padding: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ fontSize: '12px', color: '#c2185b', fontWeight: '600', textTransform: 'uppercase' }}>Commercial</span>
                          {commission.commercial_id && commission.commission_commercial > 0 && (
                            <span style={{
                              padding: '3px 10px',
                              borderRadius: '12px',
                              fontSize: '11px',
                              fontWeight: '600',
                              background: commission.commission_commercial_payee ? '#c8e6c9' : '#ffecb3',
                              color: commission.commission_commercial_payee ? '#2e7d32' : '#f57c00'
                            }}>
                              {commission.commission_commercial_payee ? '✓ Payée' : 'En attente'}
                            </span>
                          )}
                        </div>
                        <div style={{ fontWeight: '600', fontSize: '15px', color: '#333', marginBottom: '5px' }}>
                          {commission.commercial_nom || <span style={{ color: '#999', fontStyle: 'italic' }}>Non assigné</span>}
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '700', color: '#c2185b' }}>
                          {parseFloat(commission.commission_commercial || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                        </div>
                        {currentUser && currentUser.role === 'admin' && commission.commercial_id && commission.commission_commercial > 0 && !commission.commission_commercial_payee && (
                          <button
                            onClick={() => handleMarkCommissionPaidCommercial(commission.id)}
                            style={{
                              marginTop: '10px',
                              background: '#c2185b',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              width: '100%'
                            }}
                          >
                            ✓ Marquer payée
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Actions admin */}
                    {currentUser && currentUser.role === 'admin' && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e9ecef' }}>
                        {!commission.commission_payee &&
                         commission.agent_id && commission.commission_agent > 0 && !commission.commission_agent_payee &&
                         commission.commercial_id && commission.commission_commercial > 0 && !commission.commission_commercial_payee && (
                          <button
                            onClick={() => handleMarkCommissionPaid(commission.id)}
                            style={{
                              background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '6px',
                              fontSize: '13px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            ✓✓ Payer les deux
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteCommission(commission.id, commission.client_nom)}
                          style={{
                            background: '#fff',
                            color: '#e74c3c',
                            border: '1px solid #e74c3c',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                        >
                          <Trash2 size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Modal Fiche Utilisateur */}
          {selectedUserFiche && (
            <div
              onClick={() => setSelectedUserFiche(null)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '20px'
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: 'white',
                  borderRadius: '20px',
                  width: '95%',
                  maxWidth: '1200px',
                  maxHeight: '95vh',
                  overflow: 'auto',
                  boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
                  animation: 'slideIn 0.3s ease'
                }}
              >
                {loadingUserFiche ? (
                  <div style={{ padding: '80px', textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', marginBottom: '15px' }}>Chargement...</div>
                  </div>
                ) : (
                  <>
                    {/* Header avec gradient */}
                    <div style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      padding: '30px 40px',
                      borderRadius: '20px 20px 0 0',
                      color: 'white',
                      position: 'relative'
                    }}>
                      <button
                        onClick={() => setSelectedUserFiche(null)}
                        style={{
                          position: 'absolute',
                          top: '20px',
                          right: '20px',
                          background: 'rgba(255,255,255,0.2)',
                          border: 'none',
                          color: 'white',
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          fontSize: '24px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
                      >
                        &times;
                      </button>
                      <h2 style={{ margin: 0, fontSize: '28px', fontWeight: '700' }}>
                        {selectedUserFiche.user.nom}
                      </h2>
                      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '6px 16px',
                          borderRadius: '20px',
                          fontSize: '14px',
                          fontWeight: '600',
                          background: 'rgba(255,255,255,0.2)',
                          color: 'white'
                        }}>
                          {selectedUserFiche.user.role === 'agent' ? 'Agent Téléphonique' : selectedUserFiche.user.role === 'chef_equipe' ? 'Chef d\'Equipe' : selectedUserFiche.user.role === 'sous_admin' ? 'Sous-Admin' : selectedUserFiche.user.role === 'superviseur' || selectedUserFiche.user.role === 'superviseur_agent' ? 'Superviseur' : 'Commercial'}
                        </span>
                        <span style={{ fontSize: '14px', opacity: 0.9 }}>
                          {selectedUserFiche.user.email}
                        </span>
                      </div>
                    </div>

                    {/* Contenu principal */}
                    <div style={{ padding: '30px 40px' }}>
                      {/* Stats résumé - plus grands */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '30px' }}>
                        <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '25px', borderRadius: '16px', textAlign: 'center', color: 'white', boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)' }}>
                          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Taux Commission</div>
                          <div style={{ fontSize: '28px', fontWeight: '700' }}>
                            {parseFloat(selectedUserFiche.user.taux_commission || 0) > 0 ? `${parseFloat(selectedUserFiche.user.taux_commission).toFixed(2)}%` :
                             parseFloat(selectedUserFiche.user.commission_fixe || 0) > 0 ? `${parseFloat(selectedUserFiche.user.commission_fixe).toFixed(0)}€` : '-'}
                          </div>
                        </div>
                        <div style={{ background: 'linear-gradient(135deg, #27ae60 0%, #2ecc71 100%)', padding: '25px', borderRadius: '16px', textAlign: 'center', color: 'white', boxShadow: '0 8px 25px rgba(39, 174, 96, 0.3)' }}>
                          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Com. Payées</div>
                          <div style={{ fontSize: '28px', fontWeight: '700' }}>
                            {selectedUserFiche.user.commissions_payees.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                          </div>
                        </div>
                        <div style={{ background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)', padding: '25px', borderRadius: '16px', textAlign: 'center', color: 'white', boxShadow: '0 8px 25px rgba(243, 156, 18, 0.3)' }}>
                          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Com. à Venir</div>
                          <div style={{ fontSize: '28px', fontWeight: '700' }}>
                            {selectedUserFiche.user.commissions_a_venir.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                          </div>
                        </div>
                        <div style={{ background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)', padding: '25px', borderRadius: '16px', textAlign: 'center', color: 'white', boxShadow: '0 8px 25px rgba(156, 39, 176, 0.3)' }}>
                          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Avances</div>
                          <div style={{ fontSize: '28px', fontWeight: '700' }}>
                            {selectedUserFiche.user.total_avances.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €
                          </div>
                        </div>
                      </div>

                    {/* Bouton ajouter avance */}
                    <div style={{ marginBottom: '20px' }}>
                      <button
                        onClick={() => {
                          const montant = prompt('Montant de l\'avance (€):');
                          if (montant && parseFloat(montant) > 0) {
                            const token = localStorage.getItem('token');
                            fetch(`/api/users/${selectedUserFiche.user.id}/avances`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({ montant: parseFloat(montant), date_avance: new Date().toISOString().split('T')[0] })
                            })
                            .then(res => res.json())
                            .then(() => {
                              alert('Avance ajoutée avec succès');
                              fetchUserFiche(selectedUserFiche.user.id);
                              fetchFinancialStats();
                            })
                            .catch(err => alert('Erreur: ' + err.message));
                          }
                        }}
                        style={{
                          background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '10px 20px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                      >
                        + Ajouter une avance
                      </button>
                    </div>

                      {/* Historique des avances */}
                      {selectedUserFiche.avances && selectedUserFiche.avances.length > 0 && (
                        <div style={{ marginBottom: '30px' }}>
                          <h4 style={{ color: '#9c27b0', marginBottom: '15px', fontSize: '18px', fontWeight: '600' }}>
                            Historique des avances
                          </h4>
                          <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Date</th>
                                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>Montant</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedUserFiche.avances.map((avance, idx) => (
                                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                      {new Date(avance.date_avance).toLocaleDateString('fr-FR')}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#9c27b0', fontSize: '15px' }}>
                                      {parseFloat(avance.montant).toLocaleString('fr-FR')} €
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Bonus reçus */}
                      {/* Bonus reçus */}
                      {selectedUserFiche.bonus_recus && selectedUserFiche.bonus_recus.length > 0 && (
                        <div style={{ marginBottom: '30px' }}>
                          <h4 style={{ color: '#f39c12', marginBottom: '15px', fontSize: '18px', fontWeight: '600' }}>Bonus reçus</h4>
                          <div style={{ background: '#fff8e1', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '2px solid #ffe082' }}>
                                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Date</th>
                                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>De</th>
                                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Motif</th>
                                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>Montant</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedUserFiche.bonus_recus.map((bonus, idx) => (
                                  <tr key={idx} style={{ borderBottom: '1px solid #fff3cd' }}>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                      {new Date(bonus.created_at).toLocaleDateString('fr-FR')}
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>{bonus.chef_nom}</td>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>{bonus.motif || '-'}</td>
                                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#f39c12', fontSize: '15px' }}>
                                      +{parseFloat(bonus.montant).toLocaleString('fr-FR')} €
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Bonus donnés (chef d'équipe) */}
                      {selectedUserFiche.bonus_donnes && selectedUserFiche.bonus_donnes.length > 0 && (
                        <div style={{ marginBottom: '30px' }}>
                          <h4 style={{ color: '#e74c3c', marginBottom: '15px', fontSize: '18px', fontWeight: '600' }}>Bonus accordés à son équipe</h4>
                          <div style={{ background: '#ffebee', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '2px solid #ef9a9a' }}>
                                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Date</th>
                                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Agent</th>
                                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Motif</th>
                                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>Montant</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedUserFiche.bonus_donnes.map((bonus, idx) => (
                                  <tr key={idx} style={{ borderBottom: '1px solid #ffcdd2' }}>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                      {new Date(bonus.created_at).toLocaleDateString('fr-FR')}
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>{bonus.agent_nom}</td>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>{bonus.motif || '-'}</td>
                                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#e74c3c', fontSize: '15px' }}>
                                      -{parseFloat(bonus.montant).toLocaleString('fr-FR')} €
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Historique des commissions */}
                      <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ color: '#667eea', marginBottom: '15px', fontSize: '18px', fontWeight: '600' }}>
                          Historique des commissions ({selectedUserFiche.commissions_history.length})
                        </h4>
                        {selectedUserFiche.commissions_history.length > 0 ? (
                          <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '20px', maxHeight: '400px', overflow: 'auto', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, background: '#f8f9fa' }}>
                                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Date</th>
                                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Client</th>
                                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>Montant payé</th>
                                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>Commission</th>
                                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px' }}>Statut</th>
                                  {(selectedUserFiche.user.role === 'chef_equipe' || selectedUserFiche.user.role === 'sous_admin') && (
                                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Type</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {selectedUserFiche.commissions_history.map((comm, idx) => (
                                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                      {new Date(comm.date_paiement).toLocaleDateString('fr-FR')}
                                    </td>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                      <strong>#{comm.numero_client || '-'}</strong> {comm.client_nom}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>
                                      {parseFloat(comm.montant_paye || 0).toLocaleString('fr-FR')} €
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#667eea', fontSize: '15px' }}>
                                      {parseFloat(comm.montant || 0).toLocaleString('fr-FR')} €
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                      <span style={{
                                        padding: '5px 12px',
                                        borderRadius: '15px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        background: comm.payee ? '#e8f5e9' : '#fff3e0',
                                        color: comm.payee ? '#2e7d32' : '#f57c00'
                                      }}>
                                        {comm.payee ? '✓ Payée' : 'En attente'}
                                      </span>
                                    </td>
                                    {(selectedUserFiche.user.role === 'chef_equipe' || selectedUserFiche.user.role === 'sous_admin') && (
                                      <td style={{ padding: '12px', fontSize: '13px' }}>
                                        {comm.type_commission === 'chef_recu' ? (
                                          <span style={{ color: '#27ae60', fontWeight: '500' }}>Reçue</span>
                                        ) : (
                                          <span style={{ color: '#e74c3c' }}>→ {comm.agent_beneficiaire}</span>
                                        )}
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '40px', textAlign: 'center', color: '#999' }}>
                            Aucune commission enregistrée
                          </div>
                        )}
                      </div>

                      {/* Clients assignés */}
                      {selectedUserFiche.clients_assignes && selectedUserFiche.clients_assignes.length > 0 && (
                        <div>
                          <h4 style={{ color: '#333', marginBottom: '15px', fontSize: '18px', fontWeight: '600' }}>
                            Clients assignés ({selectedUserFiche.clients_assignes.length})
                          </h4>
                          <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '20px', maxHeight: '350px', overflow: 'auto', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ borderBottom: '2px solid #dee2e6', position: 'sticky', top: 0, background: '#f8f9fa' }}>
                                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>N°</th>
                                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Nom</th>
                                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px' }}>Entreprise</th>
                                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px' }}>Signé</th>
                                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '14px' }}>Payé</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedUserFiche.clients_assignes.map((client, idx) => (
                                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600' }}>#{client.numero_client || '-'}</td>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>{client.nom}</td>
                                    <td style={{ padding: '12px', fontSize: '14px' }}>{client.entreprise || '-'}</td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                      {client.client_signe ? <span style={{ color: '#27ae60', fontSize: '18px' }}>✓</span> : <span style={{ color: '#ccc' }}>-</span>}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                      {client.paye ? (
                                        <span style={{ color: '#27ae60', fontWeight: '600', fontSize: '14px' }}>
                                          {parseFloat(client.montant_paye || 0).toLocaleString('fr-FR')} €
                                        </span>
                                      ) : (
                                        <span style={{ color: '#ccc' }}>-</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </main>
      ) : activeTab === 'compte' && currentUser && (currentUser.role === 'agent' || currentUser.role === 'commercial') ? (
        <main className="dashboard-main">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
            <h2 className="section-title" style={{ color: 'white', margin: 0 }}>Mon Compte</h2>
            <button
              onClick={handleRefreshStats}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <RefreshCw size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Actualiser
            </button>
          </div>

          {stats && (
            <div className="stats-cards">
              <div className="stat-card count">
                <h3>Mes Clients</h3>
                <p className="stat-number">{clients.length}</p>
              </div>
              <div className="stat-card count">
                <h3>Clients Payés</h3>
                <p className="stat-number">{clients.filter(c => c.paye).length}</p>
              </div>
              <div className="stat-card count">
                <h3>Ma Commission</h3>
                <p className="stat-number">
                  {stats.taux_commission > 0 ? (
                    <span>{stats.taux_commission}%</span>
                  ) : stats.commission_fixe > 0 ? (
                    <span style={{color: '#27ae60'}}>{stats.commission_fixe}€</span>
                  ) : (
                    <span>-</span>
                  )}
                </p>
              </div>
              <div className="stat-card money">
                <h3>Com. Payées</h3>
                <p className="stat-number">{stats.commissions_payees.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
              </div>
              <div className="stat-card money-orange">
                <h3>Com. à Venir</h3>
                <p className="stat-number">{stats.commissions_a_venir.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</p>
              </div>
            </div>
          )}

          <section className="detail-section" style={{ background: 'white', padding: '30px', borderRadius: '15px', marginTop: '30px' }}>
            <h3 style={{ color: '#667eea', fontSize: '20px', marginBottom: '20px' }}>Informations du compte</h3>
            <div className="info-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
              <div>
                <label style={{ color: '#666', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Nom</label>
                <p style={{ fontSize: '16px', fontWeight: '500', marginTop: '5px' }}>{currentUser.nom}</p>
              </div>
              <div>
                <label style={{ color: '#666', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Email</label>
                <p style={{ fontSize: '16px', fontWeight: '500', marginTop: '5px' }}>{currentUser.email}</p>
              </div>
              <div>
                <label style={{ color: '#666', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Rôle</label>
                <p style={{ fontSize: '16px', fontWeight: '500', marginTop: '5px' }}>
                  <span className={`role-badge ${getRoleBadge(currentUser.role).class}`}>
                    {getRoleBadge(currentUser.role).text}
                  </span>
                </p>
              </div>
              <div>
                <label style={{ color: '#666', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Commission</label>
                <p style={{ fontSize: '18px', fontWeight: '700', marginTop: '5px', color: '#667eea' }}>
                  {stats && stats.taux_commission > 0 ? (
                    <span>{stats.taux_commission}%</span>
                  ) : stats && stats.commission_fixe > 0 ? (
                    <span style={{color: '#27ae60'}}>{stats.commission_fixe}€</span>
                  ) : (
                    <span>-</span>
                  )}
                </p>
              </div>
              {currentUser.promo_code && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ color: '#666', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Code partenaire personnel</label>
                  <div style={{
                    marginTop: '10px',
                    padding: '15px 20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '10px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px'
                  }}>
                    <span style={{ fontSize: '24px', fontWeight: '700', color: 'white', letterSpacing: '2px' }}>
                      {currentUser.promo_code}
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(currentUser.promo_code);
                        alert('Code copié dans le presse-papiers !');
                      }}
                      style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        border: '2px solid white',
                        borderRadius: '8px',
                        color: 'white',
                        padding: '8px 15px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                      onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                    >
                      <Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Copier
                    </button>
                  </div>
                  <p style={{ fontSize: '13px', color: '#666', marginTop: '10px', fontStyle: 'italic' }}>
                    <Lightbulb size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Partagez ce code avec vos clients pour bénéficier de 100% de réduction sur l'abonnement mensuel (offre Sur Mesure avec engagement 36 mois)
                  </p>
                </div>
              )}
            </div>
          </section>

          <section style={{ background: 'white', padding: '30px', borderRadius: '15px', marginTop: '30px' }}>
            <h3 style={{ color: '#667eea', fontSize: '20px', marginBottom: '20px' }}>Historique des Commissions</h3>

            {loadingCommissions ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>Chargement...</div>
            ) : commissionsHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Aucune commission enregistrée</div>
            ) : (
              <>
                {/* Vue desktop */}
                <div className="clients-table desktop-only">
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>N° Client</th>
                        <th>Client</th>
                        <th>Montant Payé</th>
                        <th>Ma Commission</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissionsHistory.map((commission) => {
                        const maCommission = (currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin' || currentUser.role === 'superviseur_agent') ? commission.commission_agent : commission.commission_commercial;
                        return (
                          <tr key={commission.id}>
                            <td>{new Date(commission.date_paiement).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                            <td><strong>{commission.client_numero || '-'}</strong></td>
                            <td>{commission.client_nom}</td>
                            <td><strong>{parseFloat(commission.montant_paye).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</strong></td>
                            <td><strong style={{color: '#667eea', fontSize: '16px'}}>{parseFloat(maCommission || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</strong></td>
                            <td>
                              <span className={`status-badge ${
                                (currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin' || currentUser.role === 'superviseur_agent')
                                  ? (commission.commission_agent_payee ? 'status-yes' : 'status-no')
                                  : (commission.commission_commercial_payee ? 'status-yes' : 'status-no')
                              }`}>
                                {(currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin' || currentUser.role === 'superviseur_agent')
                                  ? (commission.commission_agent_payee ? '✓ Payée' : '✗ Non payée')
                                  : (commission.commission_commercial_payee ? '✓ Payée' : '✗ Non payée')
                                }
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Vue mobile */}
                <div className="clients-cards mobile-only">
                  {commissionsHistory.map((commission) => {
                    const maCommission = (currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin' || currentUser.role === 'superviseur_agent') ? commission.commission_agent : commission.commission_commercial;
                    return (
                      <div key={commission.id} className="client-card">
                        <div className="client-card-header">
                          <div>
                            <h3>{commission.client_nom}</h3>
                            <p className="client-numero">#{commission.client_numero || '-'}</p>
                          </div>
                          <span className={`status-badge ${
                            (currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin' || currentUser.role === 'superviseur_agent')
                              ? (commission.commission_agent_payee ? 'status-yes' : 'status-no')
                              : (commission.commission_commercial_payee ? 'status-yes' : 'status-no')
                          }`}>
                            {(currentUser.role === 'agent' || currentUser.role === 'chef_equipe' || currentUser.role === 'sous_admin' || currentUser.role === 'superviseur_agent')
                              ? (commission.commission_agent_payee ? '✓ Payée' : '✗ Non payée')
                              : (commission.commission_commercial_payee ? '✓ Payée' : '✗ Non payée')
                            }
                          </span>
                        </div>
                        <div className="client-card-body">
                          <div className="client-card-field">
                            <span className="field-label">Date</span>
                            <span className="field-value">{new Date(commission.date_paiement).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
                          </div>
                          <div className="client-card-field">
                            <span className="field-label">Montant Payé</span>
                            <span className="field-value highlight">{parseFloat(commission.montant_paye).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                          </div>
                          <div className="client-card-field">
                            <span className="field-label">Ma Commission</span>
                            <span className="field-value" style={{color: '#667eea', fontWeight: 'bold', fontSize: '18px'}}>{parseFloat(maCommission || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          {/* Historique des bonus reçus */}
          {myBonuses.length > 0 && (
            <section style={{ background: 'white', padding: '30px', borderRadius: '15px', marginTop: '30px' }}>
              <h3 style={{ color: '#f39c12', fontSize: '20px', marginBottom: '20px' }}><Gift size={20} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Bonus reçus</h3>
              <div className="clients-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>De</th>
                      <th>Montant</th>
                      <th>Motif</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myBonuses.map((bonus) => (
                      <tr key={bonus.id}>
                        <td>{new Date(bonus.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                        <td><strong>{bonus.chef_nom}</strong></td>
                        <td><strong style={{color: '#27ae60', fontSize: '16px'}}>+{parseFloat(bonus.montant).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</strong></td>
                        <td>{bonus.motif || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </main>
      ) : currentUser && currentUser.role === 'client' && currentUser.has_crm_access !== 1 ? (
        // Pour les clients SANS CRM, on n'affiche rien dans le main - le ClientDetail s'affichera en overlay
        null
      ) : (
        <main className="dashboard-main">
          <div className="dashboard-controls">
            <div className="search-box">
              <input
                type="text"
                placeholder="Rechercher par n° client, nom, prénom, société, email, téléphone, SIRET ou adresse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="action-buttons-row">
            {currentUser && currentUser.role === 'admin' && (
              <button
                onClick={() => setActiveTab('emails')}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '14px 28px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                }}
              >
                <span style={{ fontSize: '20px' }}><Mail size={20} /></span>
                <span>Envoyer Emails</span>
              </button>
            )}
            {currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1 && (
              <button
                onClick={() => setActiveTab('notifications')}
                style={{
                  background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '14px 28px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  boxShadow: '0 4px 15px rgba(255, 107, 107, 0.4)',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(255, 107, 107, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(255, 107, 107, 0.4)';
                }}
              >
                <span style={{ fontSize: '20px' }}><Megaphone size={20} /></span>
                <span>Envoyer Notification</span>
              </button>
            )}
            {currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1 && (
              <button
                onClick={() => setActiveTab('emails')}
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '14px 28px',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
                }}
              >
                <span style={{ fontSize: '20px' }}><Mail size={20} /></span>
                <span>Envoyer Emails</span>
              </button>
            )}
            {currentUser && currentUser.promo_code && (
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                padding: '12px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: '15px',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.8)', fontWeight: '600' }}>
                    MON CODE PARTENAIRE
                  </span>
                  <span style={{ fontSize: '20px', fontWeight: '700', color: 'white', letterSpacing: '2px' }}>
                    {currentUser.promo_code}
                  </span>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(currentUser.promo_code);
                    alert('Code copié !');
                  }}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: '2px solid white',
                    borderRadius: '8px',
                    color: 'white',
                    padding: '6px 12px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseOver={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                  onMouseOut={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                >
                  <Copy size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Copier
                </button>
              </div>
            )}
            {currentUser && (currentUser.role === 'admin' || currentUser.role === 'commercial') && (
              <>
                <input
                  id="import-clients-csv-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleImportClientsCsv}
                />
                <button
                  onClick={() => document.getElementById('import-clients-csv-input').click()}
                  disabled={importingClients}
                  style={{
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 22px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: importingClients ? 'wait' : 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.4)',
                    opacity: importingClients ? 0.6 : 1,
                    whiteSpace: 'nowrap'
                  }}
                  title="Importer un fichier CSV ou Excel - les colonnes seront détectées automatiquement"
                >
                  <Upload size={16} />
                  {importingClients ? 'Import en cours…' : 'Importer CSV/Excel'}
                </button>
              </>
            )}
            <button onClick={handleAddClient} className="btn-add">
              + Nouveau client
            </button>
            </div>
          </div>

          <div className="stats-cards">
            <div className="stat-card count">
              <h3>{currentUser && currentUser.role === 'admin' ? 'Total Clients' : 'Mes Clients'}</h3>
              <p className="stat-number">{clients.length}</p>
            </div>
            <div className="stat-card count">
              <h3>Clients Payés</h3>
              <p className="stat-number">{clients.filter(c => c.paye).length}</p>
            </div>
            {/* Masquer "Clients Signés" pour les e-shop owners */}
            {!(currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1) && (
              <div className="stat-card count">
                <h3>Clients Signés</h3>
                <p className="stat-number">{clients.filter(c => c.client_signe).length}</p>
              </div>
            )}
            {/* Masquer "Chiffre d'Affaires" pour les commerciaux et agents */}
            {!(currentUser && (currentUser.role === 'commercial' || currentUser.role === 'agent')) && (
              <div className="stat-card money">
                <h3><Coins size={20} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Chiffre d'Affaires</h3>
                <p className="stat-number">{stats && stats.ca_total ? stats.ca_total.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €' : '0,00 €'}</p>
              </div>
            )}
            <div className="stat-card count">
              <h3>Résultats</h3>
              <p className="stat-number">{filteredClients.length}</p>
            </div>
          </div>

          {/* Bouton Filtres */}
          <div className="filter-toggle-container">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-filter ${showFilters ? 'active' : ''}`}
            >
              {showFilters ? <><XCircle size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Masquer filtres</> : <><Settings size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Filtres</>}
            </button>
          </div>

          {/* Filtres conditionnels */}
          {showFilters && (
            <div className="filters-container">
              <div className="filter-group">
                <label className="filter-label">Paiement :</label>
                <div className="filter-buttons">
                  <button
                    className={`filter-btn ${filterPaye === 'tous' ? 'active' : ''}`}
                    onClick={() => setFilterPaye('tous')}
                  >
                    Tous
                  </button>
                  <button
                    className={`filter-btn ${filterPaye === 'paye' ? 'active' : ''}`}
                    onClick={() => setFilterPaye('paye')}
                  >
                    Payés
                  </button>
                  <button
                    className={`filter-btn ${filterPaye === 'non_paye' ? 'active' : ''}`}
                    onClick={() => setFilterPaye('non_paye')}
                  >
                    Non payés
                  </button>
                </div>
              </div>

              <div className="filter-group">
                <label className="filter-label">Signature :</label>
                <div className="filter-buttons">
                  <button
                    className={`filter-btn ${filterSigne === 'tous' ? 'active' : ''}`}
                    onClick={() => setFilterSigne('tous')}
                  >
                    Tous
                  </button>
                  <button
                    className={`filter-btn ${filterSigne === 'signe' ? 'active' : ''}`}
                    onClick={() => setFilterSigne('signe')}
                  >
                    Signés
                  </button>
                  <button
                    className={`filter-btn ${filterSigne === 'non_signe' ? 'active' : ''}`}
                    onClick={() => setFilterSigne('non_signe')}
                  >
                    Non signés
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="loading-state">Chargement des clients...</div>
          ) : filteredClients.length === 0 ? (
            <div className="empty-state">
              <p>Aucun client trouvé</p>
            </div>
          ) : (
            <>
              {/* Vue tableau pour desktop */}
              <div className="clients-table desktop-only">
                <table>
                  <thead>
                    <tr>
                      <th>Détail</th>
                      <th>N° Client</th>
                      <th>Nom</th>
                      <th>Entreprise</th>
                      <th>Téléphone</th>
                      <th>Email</th>
                      <th>Dernier paiement</th>
                      <th>Montant BC</th>
                      {currentUser && currentUser.role === 'admin' && <th>Agent Téléphonique</th>}
                      {currentUser && currentUser.role === 'admin' && <th>Commercial</th>}
                      <th>Payé</th>
                      <th>Signé</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClients.map((client) => (
                      <tr key={client.id}>
                        <td>
                          <button
                            onClick={() => handleViewClient(client)}
                            className="btn-view"
                          >
                            Voir détail
                          </button>
                        </td>
                        <td><strong>{client.numero_client}</strong></td>
                        <td>{client.nom}</td>
                        <td>{client.entreprise}</td>
                        <td>{client.telephone}</td>
                        <td>{client.email}</td>
                        <td>
                          {client.last_payment_at ? (
                            <div style={{fontSize: '12px'}}>
                              <div style={{fontWeight: 'bold', color: '#2e7d32'}}>
                                {parseFloat(client.last_payment_amount).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €
                              </div>
                              <div style={{color: '#666'}}>
                                {new Date(client.last_payment_at).toLocaleString('fr-FR', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'})}
                              </div>
                              {client.total_payments > 1 && (
                                <div style={{color: '#667eea', fontSize: '11px', marginTop: '2px'}}>
                                  {client.total_payments} paiements · Total : {parseFloat(client.total_paid).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €
                                </div>
                              )}
                            </div>
                          ) : (
                            <span style={{color: '#999'}}>—</span>
                          )}
                        </td>
                        <td>{client.montant_bon_commande ? `${parseFloat(client.montant_bon_commande).toLocaleString('fr-FR')} €` : '-'}</td>
                        {currentUser && currentUser.role === 'admin' && (
                          <td>{client.agent_nom || '-'}</td>
                        )}
                        {currentUser && currentUser.role === 'admin' && (
                          <td>{client.commercial_nom || '-'}</td>
                        )}
                        <td>
                          <span className={`status-badge ${client.paye ? 'status-yes' : 'status-no'}`}>
                            {client.paye ? '✓ Payé' : '✗ Non payé'}
                          </span>
                          {client.paye && client.montant_paye && (
                            <div style={{marginTop: '5px', fontSize: '12px', fontWeight: 'bold', color: '#2e7d32'}}>
                              {parseFloat(client.montant_paye).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €
                            </div>
                          )}
                        </td>
                        <td>
                          <span className={`status-badge ${client.client_signe ? 'status-yes' : 'status-no'}`}>
                            {client.client_signe ? '✓ Signé' : '✗ Non signé'}
                          </span>
                        </td>
                        <td className="actions">
                          {/* Si client signé et utilisateur est agent : afficher seulement lien de paiement */}
                          {client.client_signe && currentUser && currentUser.role === 'agent' ? (
                            <button
                              onClick={() => handleViewClient(client)}
                              className="btn-stripe"
                              style={{background: '#667eea'}}
                            >
                              <CreditCard size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Lien de paiement
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => handleEditClient(client)}
                                className="btn-edit"
                              >
                                Modifier
                              </button>
                              {currentUser && currentUser.role === 'admin' && (
                                <button
                                  onClick={() => handleDeleteClient(client.id, client.nom)}
                                  className="btn-delete"
                                >
                                  Supprimer
                                </button>
                              )}
                              <button
                                onClick={() => handleGenerateBonCommande(client.id)}
                                className="btn-bon-commande"
                                disabled={generatingInvoice[`bdc_${client.id}`]}
                              >
                                {generatingInvoice[`bdc_${client.id}`]
                                  ? 'Génération...'
                                  : (client.client_signe
                                      ? <><ClipboardList size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Envoyer bon de commande signé</>
                                      : <><ClipboardList size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Envoyer bon de commande non signé</>)
                                }
                              </button>
                              {currentUser && currentUser.role === 'admin' && client.paye && client.montant_paye && (
                                <button
                                  onClick={() => handleGenerateInvoice(client.id)}
                                  className="btn-invoice"
                                  disabled={generatingInvoice[client.id]}
                                >
                                  {generatingInvoice[client.id] ? 'Génération...' : <><FileDown size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Facture</>}
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Vue cartes pour mobile */}
              <div className="clients-cards mobile-only">
                {filteredClients.map((client) => (
                  <div key={client.id} className="client-card">
                    <div className="client-card-header">
                      <div>
                        <h3>{client.nom}</h3>
                        <p className="client-numero">#{client.numero_client}</p>
                      </div>
                      <div className="client-card-badges">
                        <span className={`status-badge ${client.paye ? 'status-yes' : 'status-no'}`}>
                          {client.paye ? '✓ Payé' : '✗ Non payé'}
                        </span>
                        <span className={`status-badge ${client.client_signe ? 'status-yes' : 'status-no'}`}>
                          {client.client_signe ? '✓ Signé' : '✗ Non signé'}
                        </span>
                      </div>
                    </div>

                    <div className="client-card-body">
                      {client.entreprise && (
                        <div className="client-card-field">
                          <span className="field-label">Entreprise</span>
                          <span className="field-value">{client.entreprise}</span>
                        </div>
                      )}
                      <div className="client-card-field">
                        <span className="field-label">Téléphone</span>
                        <span className="field-value">{client.telephone}</span>
                      </div>
                      <div className="client-card-field">
                        <span className="field-label">Email</span>
                        <span className="field-value">{client.email}</span>
                      </div>
                      <div className="client-card-field">
                        <span className="field-label">Montant BC</span>
                        <span className="field-value highlight">
                          {client.montant_bon_commande ? `${parseFloat(client.montant_bon_commande).toLocaleString('fr-FR')} €` : '-'}
                        </span>
                      </div>
                      {client.paye && client.montant_paye && (
                        <div className="client-card-field">
                          <span className="field-label">Montant payé</span>
                          <span className="field-value" style={{color: '#2e7d32', fontWeight: 'bold'}}>
                            {parseFloat(client.montant_paye).toLocaleString('fr-FR', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €
                          </span>
                        </div>
                      )}
                      {currentUser && currentUser.role === 'admin' && client.agent_nom && (
                        <div className="client-card-field">
                          <span className="field-label">Agent Téléphonique</span>
                          <span className="field-value">{client.agent_nom}</span>
                        </div>
                      )}
                      {currentUser && currentUser.role === 'admin' && client.commercial_nom && (
                        <div className="client-card-field">
                          <span className="field-label">Commercial</span>
                          <span className="field-value">{client.commercial_nom}</span>
                        </div>
                      )}
                    </div>

                    <div className="client-card-actions">
                      <button
                        onClick={() => handleViewClient(client)}
                        className="btn-view"
                      >
                        <Eye size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Détail
                      </button>
                      {/* Si client signé et utilisateur est agent : afficher seulement lien de paiement */}
                      {client.client_signe && currentUser && currentUser.role === 'agent' ? (
                        <button
                          onClick={() => handleViewClient(client)}
                          className="btn-stripe"
                          style={{background: '#667eea', flex: 1}}
                        >
                          <CreditCard size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Lien de paiement
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditClient(client)}
                            className="btn-edit"
                          >
                            <Pencil size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Modifier
                          </button>
                          <button
                            onClick={() => handleGenerateBonCommande(client.id)}
                            className="btn-bon-commande"
                            disabled={generatingInvoice[`bdc_${client.id}`]}
                          >
                            {generatingInvoice[`bdc_${client.id}`]
                              ? <><Clock size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Envoi...</>
                              : (client.client_signe
                                  ? <><ClipboardList size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> BC signé</>
                                  : <><ClipboardList size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> BC non signé</>)
                            }
                          </button>
                          {currentUser && currentUser.role === 'admin' && client.paye && client.montant_paye && (
                            <button
                              onClick={() => handleGenerateInvoice(client.id)}
                              className="btn-invoice"
                              disabled={generatingInvoice[client.id]}
                            >
                              {generatingInvoice[client.id] ? <Clock size={14} style={{display:'inline',verticalAlign:'middle'}} /> : <><FileDown size={14} style={{display:'inline',verticalAlign:'middle',marginRight:'4px'}} /> Facture</>}
                            </button>
                          )}
                          {currentUser && currentUser.role === 'admin' && (
                            <button
                              onClick={() => handleDeleteClient(client.id, client.nom)}
                              className="btn-delete"
                            >
                              <Trash2 size={14} style={{display:'inline',verticalAlign:'middle'}} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </main>
      )}

        {showModal && (
          <ClientModal
            client={editingClient}
            onClose={() => setShowModal(false)}
            onSave={handleSaveClient}
            isAdmin={currentUser && currentUser.role === 'admin'}
            currentUser={currentUser}
          />
        )}

        {/* ClientDetail global - Ne pas afficher si on est sur l'onglet "Mon Compte" en tant qu'e-shop owner */}
        {showDetail && viewingClient && !(activeTab === 'mon-compte' && currentUser && currentUser.role === 'client' && currentUser.has_crm_access === 1) && (
          <ClientDetail
            client={viewingClient}
            onClose={() => setShowDetail(false)}
            onUpdate={(client) => {
              setShowDetail(false);
              handleEditClient(client);
            }}
            isAdmin={currentUser && currentUser.role === 'admin'}
            isCommercial={currentUser && currentUser.role === 'commercial'}
            isClientView={currentUser && currentUser.role === 'client'}
          />
        )}

        <InstallPWA />
    </div>
  );
}

export default Dashboard;
